import axios from 'axios';
import Papa from 'papaparse';
import { createClient } from '@supabase/supabase-js';

// ============================================================
// CONFIGURATION
// ============================================================

// Supabase Configuration - Direct database access for reads
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.error('❌ Supabase credentials missing! Check your .env file.');
}

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

console.log('🔗 Supabase URL:', SUPABASE_URL);
console.log('✅ Supabase client initialized');

// N8N Webhook Base URL - For processing operations only
const N8N_WEBHOOK_URL = import.meta.env.VITE_N8N_WEBHOOK_URL || 'https://automation.elevatedaudience.com/webhook';

console.log('🔗 N8N Webhook URL:', N8N_WEBHOOK_URL);
console.log('📡 N8N Mode:', N8N_WEBHOOK_URL.includes('webhook-test') ? '🧪 TEST' : '🚀 PRODUCTION');

// Create axios instance for N8N webhooks
const n8nApi = axios.create({
  baseURL: N8N_WEBHOOK_URL,
  timeout: 60000, // 60s timeout for long-running QA operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
n8nApi.interceptors.request.use(
  (config) => {
    console.log(`[N8N] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
n8nApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('[API Error]', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ============================================================
// RETRY & ERROR UTILITIES
// ============================================================

/**
 * Retry a function with exponential backoff for transient errors.
 * Only retries on server errors (5xx) or network errors.
 * @param {Function} fn - Async function to execute
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retries (default: 3)
 * @param {number} options.baseDelay - Base delay in ms (default: 1000)
 * @returns {Promise<*>} Result of the function
 */
async function fetchWithRetry(fn, { maxRetries = 3, baseDelay = 1000 } = {}) {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      const isRetryable =
        (error.response?.status >= 500) ||
        !error.response ||
        error.code === 'ECONNABORTED' ||
        error.code === 'ERR_NETWORK';
      if (!isRetryable || attempt === maxRetries) throw error;
      const delay = baseDelay * Math.pow(2, attempt);
      console.warn(`[Retry] Attempt ${attempt + 1}/${maxRetries} failed, retrying in ${delay}ms...`, error.message);
      await new Promise(r => setTimeout(r, delay));
    }
  }
}

/**
 * Classify an API error into a user-friendly message and severity level.
 * @param {Error} error - The caught error
 * @returns {{ message: string, severity: 'error'|'warning'|'info' }}
 */
export function classifyApiError(error) {
  const status = error.response?.status;
  const data = error.response?.data;

  if (!error.response) {
    return { message: 'Network error. Please check your connection and try again.', severity: 'error' };
  }
  if (status === 401 || status === 403) {
    return { message: 'Authentication failed. Please log in again.', severity: 'error' };
  }
  if (status === 404) {
    return { message: 'The requested resource was not found.', severity: 'warning' };
  }
  if (status === 409 && data?.error === 'duplicate_project') {
    return { message: 'A project with this URL already exists.', severity: 'warning' };
  }
  if (status === 429) {
    return { message: 'Too many requests. Please wait a moment and try again.', severity: 'warning' };
  }
  if (status >= 500) {
    return { message: 'Server error. Please try again in a few moments.', severity: 'error' };
  }
  return { message: data?.message || error.message || 'An unexpected error occurred.', severity: 'error' };
}

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

/**
 * Get screenshot quality from localStorage and convert to numeric value
 * Handles both old format "Standard (75%)" and new format "75"
 * @returns {number} Quality value (1-100, default 90)
 */
const getScreenshotQuality = () => {
  const quality = localStorage.getItem('screenshotQuality');

  if (!quality) return 90; // Default

  // If it's the old format like "High (90%)", extract the number
  const match = quality.match(/\((\d+)%?\)/);
  if (match) {
    return parseInt(match[1]);
  }

  // If it's already a number string, parse it
  const parsed = parseInt(quality);
  if (!isNaN(parsed) && parsed >= 1 && parsed <= 100) {
    return parsed;
  }

  // Fallback
  return 90;
};

const getScanConcurrency = () => {
  const val = localStorage.getItem('scanConcurrency');
  if (!val) return 5;
  const parsed = parseInt(val);
  return (!isNaN(parsed) && parsed >= 1 && parsed <= 10) ? parsed : 5;
};

// ============================================================
// DATA TRANSFORMERS
// Transform N8N/Database format to Frontend format
// ============================================================

/**
 * Transform project from database format to frontend format
 */
const transformProject = (dbProject) => {
  if (!dbProject) return null;

  return {
    id: dbProject.id,
    name: dbProject.name,
    clientName: dbProject.client_name || dbProject.clientName || dbProject.name,
    stagingUrl: dbProject.staging_url || dbProject.stagingUrl,
    status: dbProject.status || 'pending',
    totalPages: parseInt(dbProject.total_pages) || 0,
    passedPages: parseInt(dbProject.passed_pages) || 0,
    failedPages: parseInt(dbProject.failed_pages) || 0,
    progress: parseInt(dbProject.progress) || 0,
    createdAt: dbProject.created_at || dbProject.createdAt,
    lastRun: dbProject.last_run_at || dbProject.lastRun || dbProject.updated_at,
    scheduleEnabled: dbProject.schedule_enabled ?? false,
    scheduleCron: dbProject.schedule_cron || '0 9 * * *',
    scheduleNotificationEmail: dbProject.schedule_notification_email || null,
  };
};

/**
 * Normalize visual status from DB format (PASS/FAIL/WARNING/CRITICAL/NA/N/A/PENDING/ERROR)
 * to frontend format (pass/fail/warning/pending/error/na)
 */
const normalizeVisualStatus = (status) => {
  if (!status) return null;
  const s = String(status).toUpperCase().trim();
  if (s === 'PASS') return 'pass';
  if (s === 'FAIL') return 'fail';
  if (s === 'WARNING') return 'warning';
  if (s === 'CRITICAL') return 'fail';
  if (s === 'ERROR') return 'error';
  if (s === 'PENDING') return 'pending';
  if (s === 'NA' || s === 'N/A') return 'na';
  return s.toLowerCase();
};

/**
 * Transform QA result from database format to frontend format
 */
const transformResult = (dbResult) => {
  if (!dbResult) return null;

  // Parse JSONB fields if they're strings
  const parseJSON = (val) => {
    if (!val) return {};
    if (typeof val === 'string') {
      try { return JSON.parse(val); } catch { return {}; }
    }
    return val;
  };

  const seoChecks = parseJSON(dbResult.seo_checks);
  const technicalChecks = parseJSON(dbResult.technical_checks);
  const formChecks = parseJSON(dbResult.form_checks);
  const imageChecks = parseJSON(dbResult.image_checks);
  const linkChecks = parseJSON(dbResult.link_checks);
  const visualChecks = parseJSON(dbResult.visual_checks);
  const performanceChecks = parseJSON(dbResult.performance_checks);
  const contentChecks = parseJSON(dbResult.content_checks);

  // Determine frontend status from overall_status
  let status = 'pending';
  const overallStatus = dbResult.overall_status || dbResult.overallStatus;
  if (overallStatus === 'PASS') status = 'pass';
  else if (overallStatus === 'FAIL' && (dbResult.critical_issues || dbResult.criticalIssues) > 0) status = 'fail';
  else if (overallStatus === 'FAIL') status = 'warning';
  else if (overallStatus === 'ERROR') status = 'fail';

  // Extract page path from URL
  let pagePath = dbResult.page_path || dbResult.pagePath;
  if (!pagePath && (dbResult.page_url || dbResult.pageUrl)) {
    try {
      pagePath = new URL(dbResult.page_url || dbResult.pageUrl).pathname;
    } catch {
      pagePath = '/';
    }
  }

  // Build visualQA object, then apply false-positive correction before returning
  const visualQA = {
    overallStatus: normalizeVisualStatus(dbResult.visual_overall_status),
    allIssues: dbResult.visual_issues_all || [],
    viewportLayouts: {
      desktop: {
        status: normalizeVisualStatus(dbResult.desktop_layout),
        issues: dbResult.desktop_layout_issues || [],
      },
      tablet: {
        status: normalizeVisualStatus(dbResult.tablet_layout),
        issues: dbResult.tablet_layout_issues || [],
      },
      mobile: {
        status: normalizeVisualStatus(dbResult.mobile_layout),
        issues: dbResult.mobile_layout_issues || [],
      },
    },
    hero: {
      status: normalizeVisualStatus(dbResult.hero_status),
      headlineReadable: dbResult.hero_headline_readable ?? null,
      ctaVisible: dbResult.hero_cta_visible ?? null,
      imageQuality: dbResult.hero_image_quality || null,
      issues: dbResult.hero_issues || []
    },
    typography: {
      status: normalizeVisualStatus(dbResult.typography_status),
      h1Prominent: dbResult.h1_visually_prominent ?? null,
      hierarchyVisual: dbResult.heading_hierarchy_visual ?? null,
      fontConsistency: dbResult.font_consistency ?? null,
      textReadability: dbResult.text_readability ?? null,
      issues: dbResult.typography_issues || []
    },
    spacing: {
      status: normalizeVisualStatus(dbResult.spacing_status),
      gridAlignment: dbResult.grid_alignment ?? null,
      consistentMargins: dbResult.consistent_margins ?? null,
      verticalRhythm: dbResult.vertical_rhythm ?? null,
      issues: dbResult.spacing_issues || []
    },
    buttons: {
      status: normalizeVisualStatus(dbResult.buttons_status),
      consistentStyling: dbResult.buttons_consistent_styling ?? null,
      properSizing: dbResult.buttons_proper_sizing ?? null,
      issues: dbResult.buttons_issues || []
    },
    crossViewport: {
      status: normalizeVisualStatus(dbResult.cross_viewport_status),
      brandingConsistent: dbResult.branding_consistent ?? null,
      contentParity: dbResult.content_parity ?? null,
      issues: dbResult.cross_viewport_issues || []
    },
    imagesVisual: {
      quality: dbResult.images_visual_quality || null,
      properSizing: dbResult.images_proper_sizing ?? null,
      noPlaceholders: dbResult.images_no_placeholders ?? null,
      placeholderCount: dbResult.placeholder_images_count || 0,
      issues: dbResult.images_visual_issues || []
    },
    navigation: {
      desktopStatus: normalizeVisualStatus(dbResult.navigation_desktop_status),
      mobileStatus: normalizeVisualStatus(dbResult.navigation_mobile_status),
      issues: dbResult.navigation_issues || []
    },
    footer: {
      status: normalizeVisualStatus(dbResult.footer_status),
      issues: dbResult.footer_issues || []
    },
    accessibility: {
      overall: normalizeVisualStatus(dbResult.accessibility_overall_status),
      issues: dbResult.accessibility_issues || []
    },
    other: {
      formVisual: normalizeVisualStatus(dbResult.form_visual_status),
      horizontalScroll: dbResult.has_horizontal_scroll || false,
      adminBarVisible: dbResult.admin_bar_visible || false
    },
    layoutRecommendations: (() => {
      try {
        const vc = typeof dbResult.visual_checks === 'string'
          ? JSON.parse(dbResult.visual_checks) : dbResult.visual_checks;
        return vc?.layoutRecommendations || null;
      } catch { return null; }
    })(),
    playwrightResponsiveness: (() => {
      try {
        const vc = typeof dbResult.visual_checks === 'string'
          ? JSON.parse(dbResult.visual_checks) : dbResult.visual_checks;
        return vc?.playwrightResponsiveness || null;
      } catch { return null; }
    })(),
    playwrightAccessibility: (() => {
      try {
        const vc = typeof dbResult.visual_checks === 'string'
          ? JSON.parse(dbResult.visual_checks) : dbResult.visual_checks;
        return vc?.playwrightAccessibility || null;
      } catch { return null; }
    })(),
  };

  // ── Mobile navigation false-positive correction ──
  // When the tablet screenshot clearly shows a hamburger icon (tablet nav passes)
  // but the LLM misses it in the tall mobile screenshot, propagate the tablet result.
  const tabletNavOk = visualQA.viewportLayouts.tablet.status === 'pass' ||
    visualQA.navigation.desktopStatus === 'pass';
  const mobileNavFailed = visualQA.navigation.mobileStatus === 'fail';

  if (tabletNavOk && mobileNavFailed) {
    const navIssuePattern = /mobile navigation|hamburger|menu icon|navigation missing|no visible way to access site navigation|no mobile menu|lacks.*navigation|(?=.*\bmobile\b)(?=.*\bnavigation\b)/i;

    const filterNavIssues = (issues) =>
      (issues || []).filter(issue => !navIssuePattern.test(issue));

    visualQA.navigation.mobileStatus = 'pass';
    visualQA.navigation.issues = filterNavIssues(visualQA.navigation.issues);
    visualQA.viewportLayouts.mobile.issues = filterNavIssues(visualQA.viewportLayouts.mobile.issues);
    visualQA.crossViewport.issues = filterNavIssues(visualQA.crossViewport.issues);
    visualQA.allIssues = filterNavIssues(visualQA.allIssues);

    if (visualQA.viewportLayouts.mobile.issues.length === 0) {
      visualQA.viewportLayouts.mobile.status = 'pass';
    }

    const cvBranding = visualQA.crossViewport.brandingConsistent;
    const cvParity = visualQA.crossViewport.contentParity;
    if (visualQA.crossViewport.issues.length === 0) {
      if (cvParity === false) {
        visualQA.crossViewport.contentParity = true;
      }
      visualQA.crossViewport.status = (visualQA.crossViewport.brandingConsistent !== false) ? 'pass' : 'fail';
    }
  }

  // Always recalculate overall visual status from individual checks
  const allStatuses = [
    visualQA.viewportLayouts.desktop?.status,
    visualQA.viewportLayouts.tablet?.status,
    visualQA.viewportLayouts.mobile?.status,
    visualQA.hero?.status,
    visualQA.typography?.status,
    visualQA.spacing?.status,
    visualQA.buttons?.status,
    visualQA.crossViewport?.status,
    visualQA.navigation?.desktopStatus,
    visualQA.navigation?.mobileStatus,
    visualQA.footer?.status,
    visualQA.imagesVisual?.quality ? 'pass' : null,
    visualQA.other?.formVisual,
  ].filter(Boolean);

  if (allStatuses.some(s => s === 'fail' || s === 'error')) {
    visualQA.overallStatus = 'fail';
  } else if (allStatuses.some(s => s === 'warning')) {
    visualQA.overallStatus = 'warning';
  } else if (allStatuses.every(s => s === 'pass' || s === 'na')) {
    visualQA.overallStatus = 'pass';
  }

  return {
    id: dbResult.id,
    pageId: dbResult.page_id || dbResult.pageId,
    projectId: dbResult.project_id || dbResult.projectId,
    pagePath: pagePath,
    pageName: dbResult.page_name || dbResult.pageName,
    pageUrl: dbResult.page_url || dbResult.pageUrl,
    pageStatus: dbResult.page_status || dbResult.pageStatus || null,
    status: status,
    overallStatus: overallStatus,
    clientReady: dbResult.client_ready || dbResult.clientReady || false,

    // Issue counts
    criticalIssues: dbResult.critical_issues || dbResult.criticalIssues || 0,
    majorIssues: dbResult.major_issues || dbResult.majorIssues || 0,
    minorIssues: dbResult.minor_issues || dbResult.minorIssues || 0,

    // SEO Checks - transformed to frontend format
    metaTitle: {
      status: (seoChecks.titleMatch?.status || seoChecks.titleTag?.status || 'pending').toLowerCase(),
      expected: dbResult.expected_title || seoChecks.titleMatch?.expected || seoChecks.titleTag?.expected || '',
      actual: seoChecks.titleMatch?.actual || seoChecks.titleTag?.actual || '',
      issue: dbResult.title_tag_issue || dbResult.titleTagIssue || ''
    },
    metaDescription: {
      status: (seoChecks.metaDescMatch?.status || seoChecks.metaDescription?.status || 'pending').toLowerCase(),
      expected: dbResult.expected_description || seoChecks.metaDescMatch?.expected || seoChecks.metaDescription?.expected || '',
      actual: seoChecks.metaDescMatch?.actual || seoChecks.metaDescription?.actual || '',
      issue: dbResult.meta_description_issue || dbResult.metaDescriptionIssue || ''
    },
    h1Count: (() => {
      // Source of truth: seo_checks from Claude's analysis, fallback to h1_count column
      const count = seoChecks.h1Count?.count ?? dbResult.h1_count ?? 0;
      const content = seoChecks.h1Count?.content || (dbResult.h1_content ? [dbResult.h1_content] : []);

      // Use Claude's status and issue as primary source
      let status = (seoChecks.h1Count?.status || '').toLowerCase();
      let issue = seoChecks.h1Count?.issue || '';

      // If Claude provided status, use it; otherwise calculate from count
      if (!status || status === 'unknown') {
        if (count === 1) {
          status = 'pass';
        } else if (count === 0) {
          status = 'fail';
          issue = issue || 'Missing H1 tag - every page should have exactly one H1 tag for SEO';
        } else if (count > 1) {
          status = 'fail';
          issue = issue || `Multiple H1 tags found (${count}) - pages should have only one H1 tag for proper SEO hierarchy`;
        } else {
          status = 'pending';
        }
      }

      // Ensure we have a meaningful issue message for failures
      if (status === 'fail' && !issue) {
        if (count === 0) {
          issue = 'Missing H1 tag - every page should have exactly one H1 tag for SEO';
        } else if (count > 1) {
          issue = `Multiple H1 tags found (${count}) - pages should have only one H1 tag for proper SEO hierarchy`;
        }
      }

      // Handle 'critical' status as 'fail'
      if (status === 'critical') {
        status = 'fail';
      }

      return { status, count, content, issue };
    })(),
    headingHierarchy: {
      status: (dbResult.heading_hierarchy_status || seoChecks.headingHierarchy?.status || 'pass').toLowerCase(),
      issues: dbResult.heading_hierarchy_issues || seoChecks.headingHierarchy?.issues || []
    },
    canonical: {
      status: (seoChecks.canonical?.status || seoChecks.canonicalUrl?.status || 'pass').toLowerCase(),
      url: seoChecks.canonical?.url || seoChecks.canonicalUrl?.url || ''
    },
    openGraph: {
      status: (seoChecks.openGraph?.missing?.length > 0) ? 'fail' : 'pass',
      missing: seoChecks.openGraph?.missing || []
    },

    // Link Checks - Status based on broken link count only (security issues shown separately)
    brokenLinks: {
      status: (linkChecks.brokenCount || 0) > 0 ? 'fail' : 'pass',
      count: linkChecks.brokenCount || 0,
      links: linkChecks.brokenLinks || [],
      externalLinks: linkChecks.externalLinks || dbResult.external_links || 0,
      internalLinks: linkChecks.internalLinks || 0,
      totalLinks: linkChecks.totalLinks || 0,
      securityIssues: linkChecks.missingNoopener || 0,
      linksWithoutNoopener: linkChecks.linksWithoutNoopener || [],
      hasSecurityIssue: linkChecks.securityIssue || linkChecks.missingNoopener > 0 || dbResult.links_security_issue || false,
      issue: (linkChecks.brokenCount || 0) > 0 ? `${linkChecks.brokenCount} broken link(s) found` : ''
    },

    // Link Security (based on actual data structure)
    linkSecurity: {
      status: (linkChecks.missingNoopener || 0) > 0 || linkChecks.securityIssue ? 'fail' : 'pass',
      issues: linkChecks.missingNoopener || 0,
      linksWithoutNoopener: linkChecks.linksWithoutNoopener || [],
      description: linkChecks.issue || 'External links missing rel="noopener noreferrer"',
      missingNewTab: linkChecks.missingNewTab || 0
    },

    // Image Checks
    imagesAlt: {
      status: (imageChecks.missingAlt || imageChecks.missingAltText || 0) > 0 ? 'fail' : 'pass',
      total: imageChecks.total || imageChecks.totalImages || 0,
      missing: imageChecks.missingAlt || imageChecks.missingAltText || 0
    },

    // Content Match - from content_checks JSONB (Playwright content comparison)
    contentMatch: (() => {
      // Get the playwrightCheck from content_checks, or use content_checks directly
      const check = contentChecks.playwrightCheck || contentChecks || {};
      const rawStatus = (check.status || '').toUpperCase();

      // Map status: PASS -> pass, FAIL -> fail, SKIPPED -> skipped, else pending
      let status = 'pending';
      if (rawStatus === 'PASS') status = 'pass';
      else if (rawStatus === 'FAIL') status = 'fail';
      else if (rawStatus === 'SKIPPED') status = 'skipped';
      else if (rawStatus === 'ERROR') status = 'error';

      return {
        status,
        matchPercentage: check.matchPercentage ?? null,
        matchedWordCount: check.matchedWordCount || 0,
        matchedPhrases: check.matchedPhrases || [],
        missingPhrases: check.missingPhrases || [],
        matchedWords: check.matchedWords || [],
        unmatchedWords: check.unmatchedWords || [],
        extraWords: check.extraWords || [],
        wordCountExpected: check.wordCountExpected || 0,
        wordCountActual: check.wordCountActual || 0,
        contentChecked: check.contentChecked || false,
        issue: check.reason || check.issue || '',
        summary: check.summary || '',
        expectedPreview: check.expectedContentPreview || '',
        actualPreview: check.actualContentPreview || ''
      };
    })(),

    // Performance - transformed to frontend format
    performance: {
      mobile: performanceChecks.mobile?.performance || performanceChecks.mobilePerformance || null,
      desktop: performanceChecks.desktop?.performance || performanceChecks.desktopPerformance || null,
      accessibility: performanceChecks.mobile?.accessibility || performanceChecks.mobileAccessibility || null,
      seo: performanceChecks.mobile?.seo || performanceChecks.mobileSeo || null
    },

    // Screenshots - map from database column names
    screenshots: {
      desktop: dbResult.desktop_screenshot_url || dbResult.screenshot_desktop_url || dbResult.screenshotDesktopUrl || null,
      tablet: dbResult.tablet_screenshot_url || dbResult.screenshot_tablet_url || dbResult.screenshotTabletUrl || null,
      mobile: dbResult.mobile_screenshot_url || dbResult.screenshot_mobile_url || dbResult.screenshotMobileUrl || null
    },

    visualQA,

    // Additional data
    updatedAt: dbResult.updated_at || dbResult.updatedAt || dbResult.timestamp || dbResult.created_at,
    criticalIssuesList: dbResult.critical_issues_list || dbResult.criticalIssuesList || [],
    majorIssuesList: dbResult.major_issues_list || dbResult.majorIssuesList || [],
    minorIssuesList: dbResult.minor_issues_list || dbResult.minorIssuesList || [],
    summary: dbResult.summary || dbResult.issues_summary || '',
    recommendations: dbResult.recommendations || [],
    testerNotes: dbResult.tester_notes || dbResult.testerNotes || '',
    timestamp: dbResult.timestamp || dbResult.created_at
  };
};

/**
 * Transform page from database format to frontend format
 */
const transformPage = (dbPage) => {
  if (!dbPage) return null;

  return {
    id: dbPage.id,
    projectId: dbPage.project_id || dbPage.projectId,
    pageUrl: dbPage.page_url || dbPage.pageUrl,
    pageName: dbPage.page_name || dbPage.pageName,
    pagePath: dbPage.page_path || dbPage.pagePath,
    expectedTitle: dbPage.expected_title || dbPage.metaTitle || '',
    expectedDescription: dbPage.expected_description || dbPage.metaDescription || '',
    slug: dbPage.expected_slug || dbPage.slug || '',
    contentDocLink: dbPage.content_doc_link || dbPage.copyDocUrl || '',
    status: dbPage.status || 'pending',
    readyToPublish: dbPage.ready_to_publish || dbPage.readyToPublish || 'PENDING',
    createdAt: dbPage.created_at || dbPage.createdAt,
  };
};

// ============================================================
// PROJECT SERVICE - N8N Webhooks
// ============================================================

export const projectService = {
  /**
   * Get all projects with aggregated stats
   * Uses Supabase direct connection (faster than n8n webhook)
   * Note: Projects table has auto-updating aggregate fields via database triggers
   */
  getProjects: async (filters = {}) => {
    return fetchWithRetry(async () => {
      try {
        console.log('[SUPABASE] GET projects');

        // Query projects - aggregate stats are already calculated by DB triggers
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .neq('status', 'archived')
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Transform to frontend format - DB already has the stats!
        return data.map(transformProject);
      } catch (error) {
        console.error('[SUPABASE Error] Failed to fetch projects:', error);
        throw error;
      }
    }, { maxRetries: 2 });
  },

  /**
   * Get single project by ID with aggregated stats
   * Uses Supabase direct connection (faster than n8n webhook)
   * Note: Projects table has auto-updating aggregate fields via database triggers
   */
  getProject: async (projectId) => {
    try {
      console.log('[SUPABASE] GET project:', projectId);

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Project not found');

      // Transform to frontend format - DB already has the stats!
      return transformProject(data);
    } catch (error) {
      console.error('[SUPABASE Error] Failed to fetch project:', error);
      throw error;
    }
  },

  /**
   * Get project summary with aggregated metrics from Supabase
   * Uses project_summary view for comprehensive stats
   */
  getProjectSummary: async (projectId) => {
    try {
      console.log('[SUPABASE] GET project summary from project_summary view:', projectId);

      const { data, error } = await supabase
        .from('project_summary')
        .select('*')
        .eq('id', projectId)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Project summary not found');

      return data;
    } catch (error) {
      console.error('[SUPABASE Error] Failed to fetch project summary:', error);
      throw error;
    }
  },

  /**
   * Get all project summaries with aggregated metrics from Supabase
   * Uses project_summary view for comprehensive stats
   */
  getAllProjectSummaries: async () => {
    try {
      console.log('[SUPABASE] GET all project summaries from project_summary view');

      const { data, error } = await supabase
        .from('project_summary')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('[SUPABASE Error] Failed to fetch project summaries:', error);
      throw error;
    }
  },

  /**
   * Create new project and start QA
   * N8N Webhook: POST /qa/start
   */
  createProject: async (projectData) => {
    try {
      // Get email settings from localStorage
      const emailOnCriticalIssues = localStorage.getItem('emailOnCriticalIssues');
      const emailEnabled = emailOnCriticalIssues !== null ? JSON.parse(emailOnCriticalIssues) : true;
      const notificationEmail = localStorage.getItem('notificationEmail') || '';

      const sendCompletionSummary = localStorage.getItem('sendCompletionSummary');
      const summaryEnabled = sendCompletionSummary !== null ? JSON.parse(sendCompletionSummary) : true;

      // Format data for N8N webhook
      const payload = {
        project_name: projectData.name || projectData.projectName,
        client_name: projectData.clientName || projectData.client_name,
        staging_url: projectData.stagingUrl || projectData.staging_url,
        pages: projectData.pages || [],
        screenshot_quality: projectData.screenshotQuality || getScreenshotQuality(),
        email_on_critical_issues: emailEnabled,
        notification_email: notificationEmail,
        send_completion_summary: summaryEnabled,
        callback_url: projectData.callbackUrl || null
      };

      console.log('📸 Screenshot Quality:', payload.screenshot_quality);
      console.log('📧 Email on Critical Issues:', emailEnabled);

      const response = await n8nApi.post('/qa/start', payload);

      // Check if the response indicates an error (even if HTTP status is 200)
      if (response.data && response.data.success === false) {
        console.error('❌ API returned error:', response.data);
        const error = new Error(response.data.message || 'Project creation failed');
        error.response = { data: response.data };
        throw error;
      }

      return {
        success: true,
        projectId: response.data.project_id,
        message: response.data.message || 'QA started successfully',
        pagesQueued: response.data.pages_queued || 0
      };
    } catch (error) {
      console.error('Failed to create project:', error);
      throw error;
    }
  },

  /**
   * Update project
   * N8N Webhook: PATCH /projects/:projectId
   */
  updateProject: async (projectId, updates) => {
    try {
      const response = await n8nApi.patch(`/projects/${projectId}`, updates);
      return transformProject(response.data);
    } catch (error) {
      console.error('Failed to update project:', error);
      throw error;
    }
  },

  /**
   * Update project schedule settings (opt-in for scheduled QA)
   * Uses Supabase direct connection
   */
  updateProjectSchedule: async (projectId, { scheduleEnabled, scheduleCron, scheduleNotificationEmail }) => {
    try {
      const updates = {};
      if (typeof scheduleEnabled === 'boolean') updates.schedule_enabled = scheduleEnabled;
      if (typeof scheduleCron === 'string') updates.schedule_cron = scheduleCron;
      if (scheduleNotificationEmail !== undefined) updates.schedule_notification_email = scheduleNotificationEmail || null;

      const { data, error } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', projectId)
        .select()
        .single();

      if (error) throw error;
      return transformProject(data);
    } catch (error) {
      console.error('[SUPABASE Error] Failed to update project schedule:', error);
      throw error;
    }
  },

  /**
   * Delete project
   * N8N Webhook: DELETE /projects/:projectId
   */
  deleteProject: async (projectId) => {
    try {
      console.log('🗑️ [SUPABASE] Deleting project:', projectId);

      // Delete from Supabase - cascade delete will handle pages and results
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) {
        console.error('❌ Failed to delete project:', error);
        throw error;
      }

      console.log('✅ Project deleted successfully');
      return { success: true };
    } catch (error) {
      console.error('Failed to delete project:', error);
      throw error;
    }
  },

  /**
   * Archive project
   * N8N Webhook: POST /projects/:projectId/archive
   */
  archiveProject: async (projectId) => {
    try {
      const response = await n8nApi.post(`/projects/${projectId}/archive`);
      return response.data;
    } catch (error) {
      console.error('Failed to archive project:', error);
      throw error;
    }
  },
};

// ============================================================
// PAGE SERVICE - N8N Webhooks
// ============================================================

export const pageService = {
  /**
   * Get all pages for a project
   * Uses Supabase direct connection (faster than n8n webhook)
   */
  getPages: async (projectId, filters = {}) => {
    try {
      console.log('[SUPABASE] GET pages for project:', projectId);

      const { data, error } = await supabase
        .from('pages')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return data.map(transformPage);
    } catch (error) {
      console.error('[SUPABASE Error] Failed to fetch pages:', error);
      throw error;
    }
  },

  /**
   * Get single page by ID
   * N8N Webhook: GET /projects/:projectId/pages/:pageId
   */
  getPage: async (projectId, pageId) => {
    try {
      const response = await n8nApi.get(`/projects/${projectId}/pages/${pageId}`);
      return transformPage(response.data);
    } catch (error) {
      console.error('Failed to fetch page:', error);
      throw error;
    }
  },

  /**
   * Update a page's status directly in Supabase
   * Used to mark pages as 'in_progress' before rerun so it persists across refresh
   */
  updatePageStatus: async (pageId, status) => {
    const { error } = await supabase
      .from('pages')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', pageId);
    if (error) console.error('Failed to update page status:', error);
  },

  /**
   * Parse CSV and return pages array
   * This is done client-side before sending to N8N
   */
  parseCSV: (file) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const pages = results.data.map((row, index) => ({
            page_url: row.page_url || row.url || row.URL || '',
            page_name: row.page_name || row.name || row.Name || `Page ${index + 1}`,
            page_path: row.page_path || row.path || '',
            meta_title: row.meta_title || row.title || row.Title || '',
            meta_description: row.meta_description || row.description || row.Description || '',
            slug: row.slug || row.Slug || '',
            content_doc_link: row.content_doc_link || row.copy_doc_url || row.doc_url || ''
          })).filter(p => p.page_url); // Filter out rows without URL

          resolve(pages);
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  },

  /**
   * Upload pages via CSV - parses CSV then calls createProject
   */
  uploadPages: async (projectId, csvFile) => {
    try {
      const pages = await pageService.parseCSV(csvFile);

      // If projectId exists, add pages to existing project
      // Otherwise, this would be part of createProject
      const response = await n8nApi.post(`/projects/${projectId}/pages`, { pages });
      return response.data;
    } catch (error) {
      console.error('Failed to upload pages:', error);
      throw error;
    }
  },

  /**
   * Update page
   * N8N Webhook: PATCH /projects/:projectId/pages/:pageId
   */
  updatePage: async (projectId, pageId, updates) => {
    try {
      const response = await n8nApi.patch(`/projects/${projectId}/pages/${pageId}`, updates);
      return transformPage(response.data);
    } catch (error) {
      console.error('Failed to update page:', error);
      throw error;
    }
  },

  /**
   * Delete page
   * N8N Webhook: DELETE /projects/:projectId/pages/:pageId
   */
  deletePage: async (projectId, pageId) => {
    try {
      const response = await n8nApi.delete(`/projects/${projectId}/pages/${pageId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete page:', error);
      throw error;
    }
  },

  /**
   * Check links using Playwright service
   * Called before creating project to validate links
   */
  checkLinksWithPlaywright: async (pages) => {
    try {
      console.log('[PLAYWRIGHT] Checking links for pages:', pages.length);

      const playwrightServiceUrl = import.meta.env.VITE_PLAYWRIGHT_SERVICE_URL
        || 'http://localhost:3001';

      const response = await axios.post(`${playwrightServiceUrl}/check-links`, {
        pages: pages.map(page => ({
          url: page.pageUrl,
          pageName: page.pageName
        }))
      });

      console.log('[PLAYWRIGHT] Results received:', response.data.results.length);

      return response.data.results;
    } catch (error) {
      console.error('[PLAYWRIGHT Error] Failed to check links:', error);
      throw error;
    }
  },
};

// ============================================================
// QA SERVICE - N8N Webhooks
// ============================================================

export const qaService = {
  /**
   * Start QA for a project (all pages)
   * N8N Webhook: POST /qa/start
   */
  startProjectQA: async (projectId, projectData = {}) => {
    try {
      // Get settings from localStorage
      const emailOnCriticalIssues = localStorage.getItem('emailOnCriticalIssues');
      const emailEnabled = emailOnCriticalIssues !== null ? JSON.parse(emailOnCriticalIssues) : true;
      const notificationEmail = localStorage.getItem('notificationEmail') || '';

      const sendCompletionSummary = localStorage.getItem('sendCompletionSummary');
      const summaryEnabled = sendCompletionSummary !== null ? JSON.parse(sendCompletionSummary) : true;

      const screenshotQuality = localStorage.getItem('screenshotQuality') || '90';
      const qualityValue = parseInt(screenshotQuality) || 90;

      const payload = {
        project_id: projectId,
        email_on_critical_issues: emailEnabled,
        notification_email: notificationEmail,
        send_completion_summary: summaryEnabled,
        screenshot_quality: qualityValue,
        ...projectData
      };

      console.log('📧 Starting QA with settings:', {
        projectId,
        emailOnCriticalIssues: emailEnabled,
        notificationEmail: notificationEmail,
        sendCompletionSummary: summaryEnabled,
        screenshotQuality: qualityValue
      });

      const response = await n8nApi.post('/qa/start', payload);
      return {
        success: true,
        projectId: response.data.project_id || projectId,
        message: response.data.message || 'QA started',
        pagesQueued: response.data.pages_queued || 0
      };
    } catch (error) {
      console.error('Failed to start QA:', error);
      throw error;
    }
  },

  /**
   * Start QA for specific pages
   * N8N Webhook: POST /qa/start with page_ids
   */
  startPageQA: async (projectId, pageIds) => {
    try {
      // Get settings from localStorage
      const emailOnCriticalIssues = localStorage.getItem('emailOnCriticalIssues');
      const emailEnabled = emailOnCriticalIssues !== null ? JSON.parse(emailOnCriticalIssues) : true;
      const notificationEmail = localStorage.getItem('notificationEmail') || '';

      const sendCompletionSummary = localStorage.getItem('sendCompletionSummary');
      const summaryEnabled = sendCompletionSummary !== null ? JSON.parse(sendCompletionSummary) : true;

      const screenshotQuality = localStorage.getItem('screenshotQuality') || '90';
      const qualityValue = parseInt(screenshotQuality) || 90;

      const payload = {
        project_id: projectId,
        page_ids: pageIds,
        email_on_critical_issues: emailEnabled,
        notification_email: notificationEmail,
        send_completion_summary: summaryEnabled,
        screenshot_quality: qualityValue
      };

      const response = await n8nApi.post('/qa/start', payload);
      return response.data;
    } catch (error) {
      console.error('Failed to start page QA:', error);
      throw error;
    }
  },

  /**
   * Get QA status for a project with page counts
   * Uses Supabase direct connection (faster than n8n webhook)
   */
  getQAStatus: async (projectId) => {
    return fetchWithRetry(async () => {
      try {
        console.log('[SUPABASE] GET QA status for project:', projectId);

        // Get project basic info (has aggregate stats from DB triggers)
        const { data: project, error: projectError } = await supabase
          .from('projects')
          .select('*')
          .eq('id', projectId)
          .single();

        if (projectError) throw projectError;
        if (!project) throw new Error('Project not found');

        // Get pages to count by status
        const { data: pages, error: pagesError } = await supabase
          .from('pages')
          .select('id, status')
          .eq('project_id', projectId);

        if (pagesError) throw pagesError;

        // Count pages by status (workflow uses 'in_progress', not 'processing')
        const totalPages = pages.length;
        const completedPages = pages.filter(p => ['completed', 'reviewed'].includes(p.status)).length;
        const pendingPages = pages.filter(p => p.status === 'pending').length;
        const processingPages = pages.filter(p => p.status === 'in_progress' || p.status === 'processing').length;

        return {
          projectId: project.id,
          name: project.name,
          status: project.status,
          totalPages,
          completedPages,
          pendingPages,
          processingPages,
          passedPages: project.passed_pages || 0,  // From DB aggregate
          failedPages: project.failed_pages || 0,  // From DB aggregate
          progress: project.progress || 0,         // From DB aggregate
          scheduleEnabled: project.schedule_enabled ?? false,
          scheduleCron: project.schedule_cron || '0 9 * * *',
          scheduleNotificationEmail: project.schedule_notification_email || null,
          lastRunAt: project.last_run_at || null
        };
      } catch (error) {
        console.error('[SUPABASE Error] Failed to get QA status:', error);
        throw error;
      }
    }, { maxRetries: 2 });
  },

  /**
   * Cancel stuck processing pages by marking them as 'failed' in the database.
   * Used when a workflow was stopped/interrupted and pages are stuck in 'in_progress' or 'processing' status.
   * Returns the number of pages that were reset.
   */
  cancelStuckProcessing: async (projectId) => {
    try {
      // Find all pages stuck in processing states
      const { data: stuckPages, error: fetchError } = await supabase
        .from('pages')
        .select('id, page_name, status')
        .eq('project_id', projectId)
        .in('status', ['in_progress', 'processing']);

      if (fetchError) throw fetchError;
      if (!stuckPages || stuckPages.length === 0) return { count: 0 };

      // Mark them all as 'pending' so they can be re-run
      const { error: updateError } = await supabase
        .from('pages')
        .update({ status: 'pending', updated_at: new Date().toISOString() })
        .eq('project_id', projectId)
        .in('status', ['in_progress', 'processing']);

      if (updateError) throw updateError;

      console.log(`[QA] Cancelled ${stuckPages.length} stuck pages for project ${projectId}:`, stuckPages.map(p => p.page_name));
      return { count: stuckPages.length, pages: stuckPages.map(p => p.page_name) };
    } catch (error) {
      console.error('Failed to cancel stuck processing:', error);
      throw error;
    }
  },

  /**
   * Rerun failed pages
   * Sends request to Playwright service which handles link checking + n8n forwarding server-side.
   * This survives browser refresh, navigation, and close.
   */
  rerunFailedPages: async (projectId) => {
    try {
      const emailOnCriticalIssues = localStorage.getItem('emailOnCriticalIssues');
      const emailEnabled = emailOnCriticalIssues !== null ? JSON.parse(emailOnCriticalIssues) : true;
      const notificationEmail = localStorage.getItem('notificationEmail') || '';

      const sendCompletionSummary = localStorage.getItem('sendCompletionSummary');
      const summaryEnabled = sendCompletionSummary !== null ? JSON.parse(sendCompletionSummary) : true;

      // Gather failed pages to send to the server-side orchestrator
      let failedPagesList = [];
      let failedPageIds = [];
      try {
        const results = await resultsService.getProjectResults(projectId);
        const failedResults = results.filter(r => (r.overallStatus === 'FAIL' || r.overallStatus === 'ERROR') && (r.pageUrl || r.page_url));
        failedPagesList = failedResults.map(r => ({
          page_url: r.pageUrl || r.page_url,
          page_name: r.pageName || r.page_name || 'Page'
        }));
        failedPageIds = failedResults.map(r => r.pageId || r.page_id).filter(Boolean);
      } catch (e) {
        console.warn('⚠️ Failed to gather failed pages list:', e.message);
      }

      // Reset failed page statuses to 'pending' in database for accurate progress tracking
      if (failedPageIds.length > 0) {
        console.log('🔄 Resetting', failedPageIds.length, 'failed page statuses to pending');
        const { error: resetError } = await supabase
          .from('pages')
          .update({ status: 'pending', updated_at: new Date().toISOString() })
          .in('id', failedPageIds);
        if (resetError) {
          console.warn('⚠️ Failed to reset page statuses:', resetError.message);
        }
      }

      const playwrightServiceUrl = import.meta.env.VITE_PLAYWRIGHT_SERVICE_URL || 'http://localhost:3001';

      console.log('🚀 Sending rerun to server-side orchestrator (Playwright service)');
      console.log('📸 Screenshot Quality (rerun):', getScreenshotQuality());
      console.log('📧 Email on Critical Issues (rerun):', emailEnabled);
      console.log('📧 Notification Email (rerun):', notificationEmail);
      console.log('📊 Send Completion Summary (rerun):', summaryEnabled);
      console.log('📄 Failed pages to rerun:', failedPagesList.length);

      // Fire request to Playwright service - it responds immediately and processes in background
      const response = await axios.post(`${playwrightServiceUrl}/rerun`, {
        project_id: projectId,
        pages: failedPagesList,
        n8n_webhook_url: N8N_WEBHOOK_URL,
        settings: {
          rerun_failed: true,
          concurrency: getScanConcurrency(),
          screenshot_quality: getScreenshotQuality(),
          email_on_critical_issues: emailEnabled,
          notification_email: notificationEmail,
          send_completion_summary: summaryEnabled
        }
      });

      return {
        success: true,
        projectId: response.data.project_id || projectId,
        status: response.data.status || 'processing'
      };
    } catch (error) {
      console.error('Failed to rerun failed pages:', error);
      throw error;
    }
  },

  /**
   * Rerun specific pages
   * Sends request to Playwright service which handles link checking + n8n forwarding server-side.
   * This survives browser refresh, navigation, and close.
   */
  rerunPages: async (projectId, pageIds) => {
    try {
      const emailOnCriticalIssues = localStorage.getItem('emailOnCriticalIssues');
      const emailEnabled = emailOnCriticalIssues !== null ? JSON.parse(emailOnCriticalIssues) : true;
      const notificationEmail = localStorage.getItem('notificationEmail') || '';

      const sendCompletionSummary = localStorage.getItem('sendCompletionSummary');
      const summaryEnabled = sendCompletionSummary !== null ? JSON.parse(sendCompletionSummary) : true;

      // Gather page info for the specific page IDs
      let pagesList = [];
      try {
        const { data: pagesData } = await supabase
          .from('pages')
          .select('id, page_url, page_name')
          .eq('project_id', projectId)
          .in('id', pageIds);
        if (pagesData?.length > 0) {
          pagesList = pagesData.map(p => ({
            page_url: p.page_url,
            page_name: p.page_name || 'Page'
          }));
        }
      } catch (e) {
        console.warn('⚠️ Failed to gather pages list:', e.message);
      }

      // Reset page statuses to 'pending' in database for accurate progress tracking
      if (pageIds.length > 0) {
        console.log('🔄 Resetting', pageIds.length, 'page statuses to pending');
        const { error: resetError } = await supabase
          .from('pages')
          .update({ status: 'pending', updated_at: new Date().toISOString() })
          .in('id', pageIds);
        if (resetError) {
          console.warn('⚠️ Failed to reset page statuses:', resetError.message);
        }
      }

      const playwrightServiceUrl = import.meta.env.VITE_PLAYWRIGHT_SERVICE_URL || 'http://localhost:3001';

      console.log('🚀 Sending rerun (specific pages) to server-side orchestrator');
      console.log('📸 Screenshot Quality (rerun):', getScreenshotQuality());
      console.log('📧 Email on Critical Issues (rerun):', emailEnabled);
      console.log('📧 Notification Email (rerun):', notificationEmail);
      console.log('📊 Send Completion Summary (rerun):', summaryEnabled);
      console.log('📄 Pages to rerun:', pagesList.length);

      // Fire request to Playwright service - it responds immediately and processes in background
      const response = await axios.post(`${playwrightServiceUrl}/rerun`, {
        project_id: projectId,
        pages: pagesList,
        n8n_webhook_url: N8N_WEBHOOK_URL,
        settings: {
          page_ids: pageIds,
          concurrency: getScanConcurrency(),
          screenshot_quality: getScreenshotQuality(),
          email_on_critical_issues: emailEnabled,
          notification_email: notificationEmail,
          send_completion_summary: summaryEnabled
        }
      });

      return {
        success: true,
        projectId: response.data.project_id || projectId,
        status: response.data.status || 'processing'
      };
    } catch (error) {
      console.error('Failed to rerun pages:', error);
      throw error;
    }
  },

  /**
   * Get active processing/pending page counts grouped by project ID.
   * Single efficient query used by QAProcessingContext to detect active jobs on reload.
   */
  getActiveProcessingCounts: async () => {
    try {
      const { data, error } = await supabase
        .from('pages')
        .select('project_id, status')
        .in('status', ['in_progress', 'processing', 'pending']);
      if (error) throw error;
      const counts = {};
      (data || []).forEach(page => {
        if (!counts[page.project_id]) {
          counts[page.project_id] = { processingPages: 0, pendingPages: 0 };
        }
        if (page.status === 'pending') counts[page.project_id].pendingPages++;
        else counts[page.project_id].processingPages++;
      });
      return counts;
    } catch (error) {
      console.error('[SUPABASE Error] Failed to get active processing counts:', error);
      return {};
    }
  },
};

// ============================================================
// RESULTS SERVICE - N8N Webhooks
// ============================================================

export const resultsService = {
  /**
   * Get all results for a project with page details
   * Uses Supabase direct connection (faster than n8n webhook)
   */
  getProjectResults: async (projectId) => {
    return fetchWithRetry(async () => {
      try {
        console.log('[SUPABASE] GET results for project:', projectId);

        const { data, error } = await supabase
          .from('qa_results')
          .select(`
            *,
            pages:page_id (
              page_url,
              page_name,
              page_path,
              status,
              expected_title,
              expected_description,
              expected_slug
            )
          `)
          .eq('project_id', projectId)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Flatten the nested page data into the result object
        const results = data.map(result => {
          const page = result.pages || {};
          const { pages: _, ...resultData } = result;
          return transformResult({
            ...resultData,
            page_url: page.page_url,
            page_name: page.page_name,
            page_path: page.page_path,
            page_status: page.status,
            expected_title: page.expected_title,
            expected_description: page.expected_description,
            expected_slug: page.expected_slug
          });
        });

        return results;
      } catch (error) {
        console.error('[SUPABASE Error] Failed to fetch results:', error);
        throw error;
      }
    }, { maxRetries: 2 });
  },

  /**
   * Get raw QA results for export (all columns, no transformation)
   * Joins with pages table to get copy_doc_url for content match reporting
   */
  getProjectResultsRaw: async (projectId) => {
    try {
      const { data, error } = await supabase
        .from('qa_results')
        .select(`
          *,
          pages:page_id (
            copy_doc_url,
            expected_content
          )
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });
      if (error) throw error;

      // Flatten the joined data and extract content match info
      const results = (data || []).map(row => {
        const page = row.pages || {};
        const { pages: _, ...resultData } = row;

        // Extract content match data from content_checks JSONB
        let contentChecks = {};
        try {
          contentChecks = typeof row.content_checks === 'string'
            ? JSON.parse(row.content_checks)
            : (row.content_checks || {});
        } catch (e) {
          contentChecks = {};
        }

        // Get playwrightCheck or use contentChecks directly
        const check = contentChecks.playwrightCheck || contentChecks || {};

        // Extract link checks data from link_checks JSONB
        let linkChecks = {};
        try {
          linkChecks = typeof row.link_checks === 'string'
            ? JSON.parse(row.link_checks)
            : (row.link_checks || {});
        } catch (e) {
          linkChecks = {};
        }

        return {
          ...resultData,
          // Add flattened content match columns for easy CSV reading
          content_doc_link: page.copy_doc_url || '',
          content_match_status: check.status || '',
          content_match_percentage: check.matchPercentage ?? '',
          content_missing_phrases_count: (check.missingPhrases || []).length,
          content_word_count_expected: check.wordCountExpected || '',
          content_word_count_actual: check.wordCountActual || '',
          content_match_issue: check.issue || check.reason || '',
          // Add flattened link check columns for easy CSV reading
          broken_links_count: linkChecks.brokenCount || (linkChecks.brokenLinks || []).length,
          broken_links_list: (linkChecks.brokenLinks || []).join(' | '),
          links_security_count: linkChecks.missingNoopener || (linkChecks.linksWithoutNoopener || []).length,
          links_security_list: (linkChecks.linksWithoutNoopener || []).join(' | ')
        };
      });

      return results;
    } catch (err) {
      console.error('[SUPABASE] getProjectResultsRaw error:', err);
      throw err;
    }
  },

  /**
   * Get results for a specific page
   * N8N Webhook: GET /projects/:projectId/pages/:pageId/results
   */
  getPageResults: async (projectId, pageId) => {
    try {
      const response = await n8nApi.get(`/projects/${projectId}/pages/${pageId}/results`);
      return transformResult(response.data);
    } catch (error) {
      console.error('Failed to fetch page results:', error);
      throw error;
    }
  },

  /**
   * Mark issue as resolved
   * N8N Webhook: POST /projects/:projectId/pages/:pageId/issues/:issueId/resolve
   */
  markIssueResolved: async (projectId, pageId, issueId) => {
    try {
      const response = await n8nApi.post(`/projects/${projectId}/pages/${pageId}/issues/${issueId}/resolve`);
      return response.data;
    } catch (error) {
      console.error('Failed to mark issue resolved:', error);
      throw error;
    }
  },

  /**
   * Add note to result
   * N8N Webhook: POST /projects/:projectId/pages/:pageId/notes
   */
  addResultNote: async (projectId, pageId, note) => {
    try {
      const response = await n8nApi.post(`/projects/${projectId}/pages/${pageId}/notes`, { note });
      return response.data;
    } catch (error) {
      console.error('Failed to add note:', error);
      throw error;
    }
  },
};

// ============================================================
// EXPORT SERVICE - N8N Webhooks
// ============================================================

export const exportService = {
  /**
   * Export project results as PDF
   * N8N Webhook: GET /projects/:projectId/export/pdf
   */
  exportPDF: async (projectId) => {
    try {
      const response = await n8nApi.get(`/projects/${projectId}/export/pdf`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('Failed to export PDF:', error);
      throw error;
    }
  },

  /**
   * Export project results as CSV
   * N8N Webhook: GET /projects/:projectId/export/csv
   */
  exportCSV: async (projectId) => {
    try {
      const response = await n8nApi.get(`/projects/${projectId}/export/csv`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('Failed to export CSV:', error);
      throw error;
    }
  },

  /**
   * Download file utility
   */
  downloadFile: (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  /**
   * Export project summary report as CSV (comprehensive - all qa_results fields)
   */
  exportProjectSummaryCSV: async (project, rawResults, onProgress) => {
    onProgress?.({ step: 'preparing', message: 'Preparing CSV data...' });

    const projectName = (project?.name || 'Project').replace(/[^a-zA-Z0-9-_]/g, '_');
    const date = new Date().toISOString().slice(0, 10);

    const toStr = (v) => {
      if (v == null) return '';
      if (Array.isArray(v)) return v.map(toStr).join(' | ');
      if (typeof v === 'object') return JSON.stringify(v);
      return String(v);
    };
    const escapeCSV = (val) => {
      const s = toStr(val);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    // Calculate statistics
    const results = rawResults || [];
    const passedCount = results.filter(r => r.overall_status === 'PASS').length;
    const failedCount = results.filter(r => r.overall_status === 'FAIL').length;
    const errorCount = results.filter(r => r.overall_status === 'ERROR').length;
    const totalCritical = results.reduce((sum, r) => sum + (r.critical_issues || 0), 0);
    const totalMajor = results.reduce((sum, r) => sum + (r.major_issues || 0), 0);
    const totalMinor = results.reduce((sum, r) => sum + (r.minor_issues || 0), 0);
    const avgPerformance = results.length > 0
      ? Math.round(results.reduce((sum, r) => sum + (r.mobile_performance || r.desktop_performance || 0), 0) / results.length)
      : 0;
    const avgAccessibility = results.length > 0
      ? Math.round(results.reduce((sum, r) => sum + (r.accessibility_score || 0), 0) / results.length)
      : 0;

    // Content Match statistics
    const contentMatchResults = results.filter(r => r.content_match_status && r.content_match_status !== 'SKIPPED');
    const contentMatchPassed = contentMatchResults.filter(r => r.content_match_status === 'PASS').length;
    const contentMatchFailed = contentMatchResults.filter(r => r.content_match_status === 'FAIL').length;
    const contentMatchWarning = contentMatchResults.filter(r => r.content_match_status === 'WARNING').length;
    const avgContentMatch = contentMatchResults.length > 0
      ? Math.round(contentMatchResults.reduce((sum, r) => sum + (r.content_match_percentage || 0), 0) / contentMatchResults.length)
      : 0;

    // Link Issues statistics
    const totalBrokenLinks = results.reduce((sum, r) => sum + (r.broken_links_count || 0), 0);
    const totalSecurityLinks = results.reduce((sum, r) => sum + (r.links_security_count || 0), 0);
    const pagesWithBrokenLinks = results.filter(r => (r.broken_links_count || 0) > 0).length;
    const pagesWithSecurityIssues = results.filter(r => (r.links_security_count || 0) > 0).length;

    // Responsiveness & Accessibility check aggregates
    const {
      buildResponsivenessCheckItems: _buildResp,
      buildAccessibilityCheckItems: _buildA11y,
      computeCheckScore: _checkScore,
      parseVisualChecks: _parseVC,
    } = await import('./qaReportHelpers.js');

    const respCheckItems = _buildResp(results);
    const a11yCheckItems = _buildA11y(results);

    // Per-viewport aggregate counts for the summary section
    const _parseVCRaw = (r) => { try { const v = r.visual_checks; return (typeof v === 'string' ? JSON.parse(v) : v) || {}; } catch { return {}; } };
    const respParsed = results.map(r => _parseVCRaw(r)?.playwrightResponsiveness || null).filter(Boolean);
    const a11yParsed = results.map(r => _parseVCRaw(r)?.playwrightAccessibility || null).filter(Boolean);
    const VPS = ['desktop', 'tablet', 'mobile'];
    const totalMissingMeta = respParsed.filter(p => p.desktop?.details?.hasViewportMeta === false).length;
    const horizScrollCounts = VPS.map(vp => respParsed.filter(p => p[vp]?.details?.hasHorizontalScroll).length);
    const contrastCounts = VPS.map(vp => a11yParsed.reduce((s, p) => s + (p[vp]?.contrastIssues || 0), 0));
    const focusCounts = VPS.map(vp => a11yParsed.reduce((s, p) => s + (p[vp]?.focusableWithoutIndicator || 0), 0));
    const totalMissingAltCSV = results.reduce((sum, r) => sum + (r.images_missing_alt || 0), 0);
    const scoredRowsCSV = results.filter(r => r.accessibility_score != null);
    const avgA11yScoreCSV = scoredRowsCSV.length > 0
      ? Math.round(scoredRowsCSV.reduce((s, r) => s + r.accessibility_score, 0) / scoredRowsCSV.length)
      : null;

    onProgress?.({ step: 'building', message: 'Building comprehensive report...' });

    const summaryRows = [
      ['==============================================================================='],
      ['QA COMPREHENSIVE REPORT - ELEVATED AUDIENCE'],
      ['==============================================================================='],
      [],
      ['PROJECT INFORMATION'],
      ['-------------------------------------------------------------------------------'],
      ['Project Name', project?.name || ''],
      ['Client', project?.clientName || project?.client_name || ''],
      ['Staging URL', project?.stagingUrl || project?.staging_url || ''],
      ['Export Date', date],
      ['Export Time', new Date().toLocaleTimeString()],
      [],
      ['EXECUTIVE SUMMARY'],
      ['-------------------------------------------------------------------------------'],
      ['Total Pages Analyzed', String(results.length)],
      ['Pages Passed', String(passedCount)],
      ['Pages Failed', String(failedCount)],
      ['Pages with Errors', String(errorCount)],
      ['Pass Rate', `${results.length > 0 ? Math.round((passedCount / results.length) * 100) : 0}%`],
      [],
      ['ISSUE BREAKDOWN'],
      ['-------------------------------------------------------------------------------'],
      ['Total Critical Issues', String(totalCritical)],
      ['Total Major Issues', String(totalMajor)],
      ['Total Minor Issues', String(totalMinor)],
      ['Total All Issues', String(totalCritical + totalMajor + totalMinor)],
      [],
      ['PERFORMANCE METRICS (AVERAGE)'],
      ['-------------------------------------------------------------------------------'],
      ['Performance Score', `${avgPerformance}/100`],
      ['Accessibility Score', `${avgAccessibility}/100`],
      [],
      ['CONTENT MATCH SUMMARY'],
      ['-------------------------------------------------------------------------------'],
      ['Pages with Content Check', String(contentMatchResults.length)],
      ['Content Match Passed', String(contentMatchPassed)],
      ['Content Match Failed', String(contentMatchFailed)],
      ['Content Match Warnings', String(contentMatchWarning)],
      ['Average Match Percentage', contentMatchResults.length > 0 ? `${avgContentMatch}%` : 'N/A'],
      [],
      ['LINK ISSUES SUMMARY'],
      ['-------------------------------------------------------------------------------'],
      ['Total Broken Links Found', String(totalBrokenLinks)],
      ['Pages with Broken Links', String(pagesWithBrokenLinks)],
      ['Total Security Issues (missing noopener)', String(totalSecurityLinks)],
      ['Pages with Security Issues', String(pagesWithSecurityIssues)],
      [],
      ['RESPONSIVENESS CHECKS SUMMARY'],
      ['-------------------------------------------------------------------------------'],
      ['Check Score', respCheckItems.length > 0 ? _checkScore(respCheckItems) : 'No data'],
      ['Pages Missing Viewport Meta Tag', String(totalMissingMeta)],
      ['Pages with Horizontal Scroll (Desktop / Tablet / Mobile)', `${horizScrollCounts[0]} / ${horizScrollCounts[1]} / ${horizScrollCounts[2]}`],
      [],
      ['ACCESSIBILITY CHECKS SUMMARY'],
      ['-------------------------------------------------------------------------------'],
      ['Check Score', a11yCheckItems.length > 0 ? _checkScore(a11yCheckItems) : 'No data'],
      ['Total Contrast Issues (Desktop / Tablet / Mobile)', `${contrastCounts[0]} / ${contrastCounts[1]} / ${contrastCounts[2]}`],
      ['Total Missing Focus Indicators (Desktop / Tablet / Mobile)', `${focusCounts[0]} / ${focusCounts[1]} / ${focusCounts[2]}`],
      ['Images Missing Alt Text', String(totalMissingAltCSV)],
      ['Average Accessibility Score', avgA11yScoreCSV != null ? `${avgA11yScoreCSV}/100` : 'N/A'],
      [],
      [],
      ['==============================================================================='],
      ['DETAILED PAGE RESULTS'],
      ['==============================================================================='],
      [],
    ];

    if (results.length === 0) {
      summaryRows.push(['No QA results available for this project.']);
      const csv = summaryRows.map((row) => row.map(escapeCSV).join(',')).join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      exportService.downloadFile(blob, `QA_Report_${projectName}_${date}.csv`);
      onProgress?.({ step: 'complete', message: 'Export complete!' });
      return;
    }

    onProgress?.({ step: 'processing', message: `Processing ${results.length} pages...` });

    const flatKeys = [
      'page_url', 'page_name', 'page_path', 'overall_status', 'client_ready',
      'critical_issues', 'major_issues', 'minor_issues', 'total_issues',
      'expected_title', 'actual_title', 'expected_description', 'actual_description',
      'title_tag_issue', 'meta_description_issue',
      'seo_status', 'h1_count', 'title_match', 'meta_desc_match',
      'heading_hierarchy_status', 'canonical_status', 'robots_status', 'noindex_warning',
      'og_complete', 'twitter_card_complete', 'schema_count',
      'has_hreflang', 'hreflang_count',
      'forms_found', 'spam_protection', 'images_total', 'images_missing_alt',
      'external_links', 'links_security_issue',
      // Content Match columns (from Playwright content comparison)
      'content_match_status', 'content_match_percentage', 'content_missing_phrases_count',
      'content_word_count_expected', 'content_word_count_actual', 'content_match_issue', 'content_doc_link',
      // Link Check columns (broken links and security issues)
      'broken_links_count', 'broken_links_list', 'links_security_count', 'links_security_list',
      'desktop_layout', 'tablet_layout', 'mobile_layout',
      'mobile_performance', 'mobile_accessibility', 'mobile_seo_score',
      'desktop_performance', 'desktop_accessibility', 'desktop_seo_score',
      'accessibility_score', 'content_word_count', 'content_quality',
      'security_status', 'has_mixed_content',
      'cwv_lcp_status', 'cwv_cls_status', 'cwv_fid_status',
      'has_cookie_consent', 'tracking_scripts_count',
      'has_errors', 'page_builder', 'seo_plugin', 'cache_plugin',
      'issues_summary', 'summary', 'tester_notes',
      'desktop_screenshot_url', 'tablet_screenshot_url', 'mobile_screenshot_url',
      'timestamp', 'created_at',
    ];
    const arrayKeys = ['h1_content', 'heading_hierarchy_issues', 'schema_types', 'error_types', 'critical_issues_list', 'major_issues_list', 'minor_issues_list'];
    const jsonKeys = ['seo_checks', 'technical_checks', 'form_checks', 'image_checks', 'link_checks', 'visual_checks', 'performance_checks', 'wordpress_checks', 'accessibility_checks', 'content_checks', 'security_checks', 'core_web_vitals_checks', 'privacy_checks', 'raw_metadata'];
    const allKeys = [...flatKeys, ...arrayKeys, ...jsonKeys];
    const headers = allKeys;

    onProgress?.({ step: 'generating', message: 'Generating CSV file...' });

    const checkDetailHeaders = ['Section', 'Check Name', 'Viewport', 'Status', 'Severity', 'Description', 'Recommended Fix'];
    const respDetailRows = respCheckItems.map(item =>
      [
        'Responsiveness', item.checkName, item.viewport, item.status,
        item.severity, item.description, item.fix,
      ].map(escapeCSV).join(',')
    );
    const a11yDetailRows = a11yCheckItems.map(item =>
      [
        'Accessibility', item.checkName, item.viewport, item.status,
        item.severity, item.description, item.fix,
      ].map(escapeCSV).join(',')
    );

    const csvRows = [
      ...summaryRows.map((row) => row.map(escapeCSV).join(',')),
      headers.join(','),
      ...results.map((r) => headers.map((h) => escapeCSV(r[h])).join(',')),
      // Responsiveness & Accessibility detail section
      '',
      '',
      escapeCSV('==============================================================================='),
      escapeCSV('RESPONSIVENESS & ACCESSIBILITY DETAIL'),
      escapeCSV('==============================================================================='),
      '',
      checkDetailHeaders.join(','),
      ...respDetailRows,
      ...a11yDetailRows,
    ];
    const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    exportService.downloadFile(blob, `QA_Report_${projectName}_${date}.csv`);

    onProgress?.({ step: 'complete', message: 'Export complete!' });
  },

  /**
   * Export project summary report as PDF with Elevated Audience template (comprehensive)
   */
  exportProjectSummaryPDF: async (project, results, rawResults, onProgress, { includeScreenshots = true } = {}) => {
    onProgress?.({ step: 'loading', message: 'Loading PDF library...' });

    const { jsPDF } = await import('jspdf');
    const projectName = (project?.name || 'Project').replace(/[^a-zA-Z0-9-_]/g, '_');
    const date = new Date().toISOString().slice(0, 10);
    const allRaw = rawResults || [];

    onProgress?.({ step: 'initializing', message: 'Initializing document...' });

    /**
     * Sanitize text for PDF rendering - fixes corrupted characters
     * jsPDF has issues with certain Unicode characters
     */
    const sanitizeText = (text) => {
      if (text == null) return '';
      let str = String(text);

      // Note: HTML entities like &amp; &lt; &gt; are intentionally NOT decoded
      // because QA findings reference them literally (e.g., "title contains &amp; instead of &")
      // and decoding would destroy the meaning. jsPDF renders plain text, not HTML.
      str = str.replace(/&nbsp;/g, ' ');

      // Replace problematic Unicode characters with ASCII equivalents
      str = str
        .replace(/→/g, '->')
        .replace(/←/g, '<-')
        .replace(/↔/g, '<->')
        .replace(/•/g, '*')
        .replace(/·/g, '-')
        .replace(/–/g, '-')
        .replace(/—/g, '-')
        .replace(/'/g, "'")
        .replace(/'/g, "'")
        .replace(/"/g, '"')
        .replace(/"/g, '"')
        .replace(/…/g, '...')
        .replace(/™/g, '(TM)')
        .replace(/®/g, '(R)')
        .replace(/©/g, '(C)')
        .replace(/°/g, ' deg')
        .replace(/±/g, '+/-')
        .replace(/×/g, 'x')
        .replace(/÷/g, '/')
        .replace(/≤/g, '<=')
        .replace(/≥/g, '>=')
        .replace(/≠/g, '!=')
        .replace(/≈/g, '~')
        .replace(/∞/g, 'infinity')
        .replace(/√/g, 'sqrt')
        .replace(/∑/g, 'sum')
        .replace(/∏/g, 'product')
        .replace(/∫/g, 'integral')
        .replace(/α/g, 'alpha')
        .replace(/β/g, 'beta')
        .replace(/γ/g, 'gamma')
        .replace(/δ/g, 'delta')
        .replace(/ε/g, 'epsilon')
        .replace(/π/g, 'pi')
        .replace(/σ/g, 'sigma')
        .replace(/τ/g, 'tau')
        .replace(/φ/g, 'phi')
        .replace(/ω/g, 'omega')
        .replace(/λ/g, 'lambda')
        .replace(/μ/g, 'mu')
        .replace(/✓/g, '[OK]')
        .replace(/✗/g, '[X]')
        .replace(/✔/g, '[OK]')
        .replace(/✘/g, '[X]')
        .replace(/★/g, '*')
        .replace(/☆/g, '*')
        .replace(/♦/g, '*')
        .replace(/♣/g, '*')
        .replace(/♠/g, '*')
        .replace(/♥/g, '*');

      // Remove any remaining non-printable or problematic characters
      // Keep only ASCII printable characters, newlines, and common extended ASCII
      str = str.replace(/[^\x20-\x7E\n\r\t]/g, ' ');

      // Clean up multiple spaces
      str = str.replace(/\s+/g, ' ').trim();

      return str;
    };

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    let y = 15;
    const margin = 20;
    const contentW = pageW - margin * 2;
    const lineH = 5; // standard line height for body text

    // Import shared report helpers
    const {
      gatherIssueDetails,
      buildIssueTitle,
      groupBySeverity,
      buildRecommendations,
      parseSummaryIntoBullets,
      fetchPageScreenshots,
      extractLayoutRecommendations,
      SEVERITY_SECTIONS,
      buildResponsivenessCheckItems,
      buildAccessibilityCheckItems,
      computeCheckScore,
      filterNegativesOnly,           // NEW - P0
      filterPagesWithIssuesOnly,     // NEW - P0
      getFailedSeoChecks,            // NEW - P0
      getCoverPageBadge,             // NEW - P1
      computeSummaryStatistics,      // NEW - P1
    } = await import('./qaReportHelpers.js');

    // Calculate comprehensive statistics
    const passedCount = allRaw.filter(r => r.overall_status === 'PASS').length;
    const failedCount = allRaw.filter(r => r.overall_status === 'FAIL').length;
    const warningCount = allRaw.filter(r => r.overall_status === 'WARNING').length;
    const totalCritical = allRaw.reduce((sum, r) => sum + (r.critical_issues || 0), 0);
    const totalMajor = allRaw.reduce((sum, r) => sum + (r.major_issues || 0), 0);
    const totalMinor = allRaw.reduce((sum, r) => sum + (r.minor_issues || 0), 0);

    // Group issues by severity for the report structure
    const issuesByLevel = groupBySeverity(allRaw);

    // ================================================================
    // Document-style helper functions (matching Word/DOCX template)
    // ================================================================

    const checkNewPage = (needed) => {
      if (y + needed > pageH - 18) {
        doc.addPage();
        y = 20;
      }
    };

    /** Title style - centered, large bold (like Word Title) */
    const addTitle = (text) => {
      checkNewPage(20);
      doc.setFontSize(22);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(sanitizeText(text), pageW / 2, y, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      y += 10;
    };

    /** Subtitle - centered, medium normal (project name, URL) */
    const addSubtitle = (text, opts = {}) => {
      checkNewPage(8);
      doc.setFontSize(opts.size || 12);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(opts.color ? opts.color[0] : 100, opts.color ? opts.color[1] : 100, opts.color ? opts.color[2] : 100);
      doc.text(sanitizeText(text), pageW / 2, y, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      y += 5;
    };

    /** Heading 1 - bold, large, with underline (like Word Heading 1) */
    const addHeading1 = (text) => {
      checkNewPage(16);
      y += 4;
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(sanitizeText(text), margin, y);
      y += 2;
      doc.setDrawColor(30, 41, 59);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageW - margin, y);
      doc.setLineWidth(0.2);
      doc.setTextColor(0, 0, 0);
      y += 4;
    };

    /** Heading 2 - bold, medium (like Word Heading 2) */
    const addHeading2 = (text) => {
      checkNewPage(12);
      y += 2;
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(51, 65, 85);
      const lines = doc.splitTextToSize(sanitizeText(text), contentW);
      lines.forEach(line => {
        checkNewPage(6);
        doc.text(line, margin, y);
        y += 6;
      });
      doc.setTextColor(0, 0, 0);
      y += 0.5;
    };

    /** Normal paragraph - wrapped text */
    const addNormal = (text, opts = {}) => {
      if (!text) return;
      checkNewPage(6);
      doc.setFontSize(10);
      doc.setFont(undefined, opts.bold ? 'bold' : opts.italic ? 'italic' : 'normal');
      doc.setTextColor(opts.color ? opts.color[0] : 55, opts.color ? opts.color[1] : 65, opts.color ? opts.color[2] : 81);
      const lines = doc.splitTextToSize(sanitizeText(text), contentW - (opts.indent || 0));
      lines.forEach(line => {
        checkNewPage(lineH);
        doc.text(line, margin + (opts.indent || 0), y);
        y += lineH;
      });
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'normal');
      y += 0.5;
    };

    /** Label: Value on same line (like "Page: dental-bridges") */
    const addLabelValue = (label, value) => {
      if (!value) return;
      checkNewPage(6);
      doc.setFontSize(10);
      const labelText = sanitizeText(`${label}: `);
      const valueText = sanitizeText(String(value));
      doc.setFont(undefined, 'bold');
      doc.setTextColor(55, 65, 81);
      doc.text(labelText, margin, y);
      const labelWidth = doc.getTextWidth(labelText);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(75, 85, 99);
      // Wrap value text if it's too long
      const availW = contentW - labelWidth;
      if (doc.getTextWidth(valueText) > availW) {
        const wrappedLines = doc.splitTextToSize(valueText, availW);
        wrappedLines.forEach((line, idx) => {
          if (idx === 0) {
            doc.text(line, margin + labelWidth, y);
          } else {
            y += lineH;
            checkNewPage(lineH);
            doc.text(line, margin + labelWidth, y);
          }
        });
      } else {
        doc.text(valueText, margin + labelWidth, y);
      }
      doc.setTextColor(0, 0, 0);
      y += lineH;
    };

    /** Bullet point - indented with dash, wrapped */
    const addBullet = (text) => {
      if (!text) return;
      checkNewPage(6);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(55, 65, 81);
      const bulletIndent = 6;
      const lines = doc.splitTextToSize(sanitizeText(text), contentW - bulletIndent - 2);
      lines.forEach((line, idx) => {
        checkNewPage(lineH);
        if (idx === 0) {
          doc.text('-', margin + 2, y);
        }
        doc.text(line, margin + bulletIndent, y);
        y += lineH;
      });
      doc.setTextColor(0, 0, 0);
    };

    /** Small spacer */
    const addSpace = (mm = 4) => { y += mm; };

    // ----------------------------------------------------------------
    // Check section renderer (Responsiveness & Accessibility)
    // ----------------------------------------------------------------
    const STATUS_COLORS_PDF = {
      Pass: [47, 191, 113],
      Fail: [229, 83, 61],
      Warning: [244, 183, 64],
    };

    /**
     * Renders a check-item section as a visual table using jsPDF drawing primitives.
     * Does nothing if items is empty.
     */
    const addCheckSection = (sectionTitle, items) => {
      if (!items || items.length === 0) return;

      addHeading1(sectionTitle);
      addNormal(computeCheckScore(items), { bold: true });
      addSpace(3);

      // Column layout (mm, relative to margin):
      // Status badge: 18mm | Check Name: 48mm | Viewport: 22mm | Severity: 18mm | Desc+Fix: rest
      const colStatus = { x: margin, w: 18 };
      const colName = { x: margin + 19, w: 48 };
      const colViewport = { x: margin + 68, w: 22 };
      const colSeverity = { x: margin + 91, w: 18 };
      const colDesc = { x: margin + 110, w: contentW - 110 };

      const headerH = 6;
      checkNewPage(headerH + 4);

      // Dark header row background
      doc.setFillColor(30, 41, 59);
      doc.rect(margin, y - 4, contentW, headerH, 'F');

      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('Status', colStatus.x + 1, y);
      doc.text('Check Name', colName.x + 1, y);
      doc.text('Viewport', colViewport.x + 1, y);
      doc.text('Severity', colSeverity.x + 1, y);
      doc.text('Description / Fix', colDesc.x + 1, y);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
      y += headerH;

      items.forEach((item, rowIdx) => {
        // Build description+fix first so we can size the row dynamically
        const descFix = item.description
          ? (item.fix ? `${item.description} | Fix: ${item.fix}` : item.description)
          : '';
        const descLine = doc.splitTextToSize(sanitizeText(descFix), colDesc.w - 2);
        const numDescLines = descLine.length;
        const rowH = Math.max(7, numDescLines * 3.5 + 3);
        checkNewPage(rowH + 2);

        // Alternating row tint
        if (rowIdx % 2 === 0) {
          doc.setFillColor(248, 250, 252);
          doc.rect(margin, y - 4.5, contentW, rowH, 'F');
        }

        // Status badge (colored rectangle)
        const statusRgb = STATUS_COLORS_PDF[item.status] || [150, 150, 150];
        doc.setFillColor(...statusRgb);
        doc.rect(colStatus.x, y - 4.5, colStatus.w - 1, rowH, 'F');

        doc.setFontSize(7.5);

        // Status text (white on badge)
        doc.setFont(undefined, 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(sanitizeText(item.status), colStatus.x + 1, y);

        // Other cells
        doc.setFont(undefined, 'normal');
        doc.setTextColor(55, 65, 81);

        const nameLine = doc.splitTextToSize(sanitizeText(item.checkName), colName.w - 2);
        doc.text(nameLine[0] || '', colName.x + 1, y);
        doc.text(sanitizeText(item.viewport), colViewport.x + 1, y);
        doc.text(sanitizeText(item.severity), colSeverity.x + 1, y);

        // Description + fix — render all lines (full text, no truncation)
        doc.text(descLine, colDesc.x + 1, y);

        // Separator line
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.1);
        doc.line(margin, y + rowH - 4.5, pageW - margin, y + rowH - 4.5);

        doc.setTextColor(0, 0, 0);
        y += rowH;
      });

      addSpace(4);
    };

    // ================================================================
    // BUILD THE DOCUMENT (matching Bozeman DOCX template exactly)
    // ================================================================

    onProgress?.({ step: 'cover', message: 'Creating title page...' });

    // ---- TITLE PAGE ----
    y = 35;
    addTitle('QA Review Report');
    addSpace(2);
    addSubtitle(`${project?.name || 'Project'} Development Site`);

    // Date
    const prettyDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
    addSubtitle(prettyDate, { size: 10, color: [100, 116, 139] });
    addSpace(2);

    // Issue summary badge
    const badge = getCoverPageBadge(allRaw);
    checkNewPage(12);
    doc.setFillColor(239, 246, 255); // Light blue background
    doc.roundedRect(margin + 20, y - 2, contentW - 40, 10, 2, 2, 'F');
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin + 20, y - 2, contentW - 40, 10, 2, 2, 'S');
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 64, 175);
    doc.text(sanitizeText(badge), pageW / 2, y + 4, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
    y += 12;
    addSpace(1);

    // Staging URL
    const stagingUrl = project?.stagingUrl || project?.staging_url || '';
    if (stagingUrl) {
      addSubtitle(stagingUrl, { size: 10, color: [59, 130, 246] });
      addSpace(1);
    }

    // Tester name
    const testerName = project?.tester_name || project?.created_by;
    if (testerName) {
      checkNewPage(6);
      doc.setFontSize(10);
      doc.setTextColor(55, 65, 81);
      const testerLabel = 'Tester: ';
      const labelWidth = doc.getTextWidth(testerLabel);
      const nameWidth = doc.getTextWidth(sanitizeText(testerName));
      const totalWidth = labelWidth + nameWidth;
      const startX = (pageW - totalWidth) / 2;

      doc.setFont(undefined, 'bold');
      doc.text(sanitizeText(testerLabel), startX, y);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(107, 114, 128);
      doc.text(sanitizeText(testerName), startX + labelWidth, y);
      doc.setTextColor(0, 0, 0);
      y += 6;
    }

    // ---- TABLE OF CONTENTS ----
    onProgress?.({ step: 'toc', message: 'Building table of contents...' });
    addSpace(4);
    addHeading1('Table of Contents');
    addSpace(1);

    // Build TOC items based on what sections will appear
    const tocItems = ['Executive Summary'];
    if (issuesByLevel.critical.length > 0) tocItems.push('Critical Issues (Must Fix Before Launch)');
    if (issuesByLevel.major.length > 0) tocItems.push('Major Issues (High Priority)');
    if (issuesByLevel.moderate.length > 0) tocItems.push('Moderate Issues');
    if (issuesByLevel.minor.length > 0) tocItems.push('Minor Issues (Low Priority)');

    // Check for responsiveness and accessibility failures inline (variables declared later in their respective sections)
    const hasRespFailures = filterNegativesOnly(buildResponsivenessCheckItems(allRaw)).length > 0;
    const hasA11yFailures = filterNegativesOnly(buildAccessibilityCheckItems(allRaw)).length > 0;
    if (hasRespFailures) tocItems.push('Responsiveness Checks');
    if (hasA11yFailures) tocItems.push('Accessibility Checks');
    tocItems.push('Additional Recommendations');
    tocItems.push('Priority Action Items');
    tocItems.push('Summary Statistics');

    tocItems.forEach(item => {
      checkNewPage(lineH);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(55, 65, 81);
      doc.text(sanitizeText(`• ${item}`), margin + 2, y);
      y += lineH;
    });

    addSpace(4);

    // ---- EXECUTIVE SUMMARY ----
    onProgress?.({ step: 'summary', message: 'Writing executive summary...' });

    addSpace(6);
    addHeading1('Executive Summary');

    const passRate = allRaw.length > 0 ? Math.round((passedCount / allRaw.length) * 100) : 0;
    const summaryText = `This report documents quality assurance issues found during a comprehensive review of all ${allRaw.length} pages on the ${project?.name || 'Project'} development website. Issues are categorized by severity level and organized by page location.`;
    addNormal(summaryText);
    addSpace(2);

    addLabelValue('Total Pages Reviewed', String(allRaw.length));
    addLabelValue('Passed', `${passedCount} pages`);
    addLabelValue('Failed', `${failedCount} pages`);
    if (warningCount > 0) {
      addLabelValue('Warnings', `${warningCount} pages`);
    }
    addLabelValue('Pass Rate', `${passRate}%`);
    addSpace(2);
    addLabelValue('Critical Issues', String(totalCritical));
    addLabelValue('Major Issues', String(totalMajor));
    addLabelValue('Minor Issues', String(totalMinor));

    // ---- ISSUES BY SEVERITY ----
    onProgress?.({ step: 'details', message: 'Writing detailed findings...' });

    let issueNumber = 0;
    for (const { key, title } of SEVERITY_SECTIONS) {
      const issues = issuesByLevel[key];
      if (issues.length === 0) continue;

      addHeading1(title);

      for (const raw of issues) {
        issueNumber++;
        const issueTitle = `${issueNumber}. ${buildIssueTitle(raw)}`;

        addHeading2(issueTitle);

        // Page info
        addLabelValue('Page', raw.page_name || raw.page_path || 'Unknown');
        if (raw.page_url) {
          addLabelValue('URL', raw.page_url);
        }

        // Description (parsed AI summary -- shown first, matching frontend)
        const summaryText = raw.summary || raw.issues_summary;
        if (summaryText) {
          const summaryBullets = parseSummaryIntoBullets(summaryText);
          addNormal('Description:', { bold: true });
          summaryBullets.forEach(item => addBullet(item));
        }

        // Issue count
        const issueSummaryParts = [];
        if ((raw.critical_issues || 0) > 0) issueSummaryParts.push(`${raw.critical_issues} critical`);
        if ((raw.major_issues || 0) > 0) issueSummaryParts.push(`${raw.major_issues} major`);
        if ((raw.minor_issues || 0) > 0) issueSummaryParts.push(`${raw.minor_issues} minor`);
        if (issueSummaryParts.length > 0) {
          addLabelValue('Issue Count', issueSummaryParts.join(', '));
        }

        // Issue details by severity (from pre-categorized lists)
        const criticalList = Array.isArray(raw.critical_issues_list) ? raw.critical_issues_list : [];
        const majorList = Array.isArray(raw.major_issues_list) ? raw.major_issues_list : [];
        const minorList = Array.isArray(raw.minor_issues_list) ? raw.minor_issues_list : [];

        if (criticalList.length > 0) {
          addNormal('Critical Issues:', { bold: true, color: [220, 38, 38] });
          criticalList.forEach(issue => addBullet(issue));
        }
        if (majorList.length > 0) {
          addNormal('Major Issues:', { bold: true, color: [217, 119, 6] });
          majorList.forEach(issue => addBullet(issue));
        }
        if (minorList.length > 0) {
          addNormal('Minor Issues:', { bold: true, color: [161, 98, 7] });
          minorList.forEach(issue => addBullet(issue));
        }

        // Specific Problems (detailed breakdown from checks)
        const details = gatherIssueDetails(raw);
        if (details.length > 0) {
          addNormal('Specific Problems:', { bold: true });
          details.forEach(item => addBullet(item));
        }

        // Per-page Design & UX Recommendations
        const layoutRec = extractLayoutRecommendations(raw);
        if (layoutRec && (layoutRec.strengths?.length > 0 || layoutRec.suggestions?.length > 0 || layoutRec.bestPracticesMissing?.length > 0)) {
          addSpace(2);
          addHeading2('Design & UX Recommendations');
          const ratingLabel = (layoutRec.overallRating || '').replace(/_/g, ' ');
          if (ratingLabel) addLabelValue('Rating', ratingLabel.charAt(0).toUpperCase() + ratingLabel.slice(1));
          if (layoutRec.engagementScore != null) addLabelValue('Engagement Score', `${layoutRec.engagementScore}/10`);

          if (layoutRec.strengths?.length > 0) {
            addNormal('Strengths:', { bold: true, color: [34, 139, 34] });
            layoutRec.strengths.forEach(s => addBullet(s));
          }
          if (layoutRec.suggestions?.length > 0) {
            addNormal('Suggestions:', { bold: true });
            layoutRec.suggestions.forEach(s => {
              const label = s.area ? `${s.area}${s.impact ? ` (${s.impact})` : ''}` : '';
              addBullet(`${label}${label ? ' — ' : ''}${s.recommendation || s.current || ''}`);
            });
          }
          if (layoutRec.bestPracticesMissing?.length > 0) {
            addNormal('Missing Best Practices:', { bold: true, color: [217, 119, 6] });
            layoutRec.bestPracticesMissing.forEach(bp => addBullet(bp));
          }
        }

        // Screenshots -- one per page, placed after Missing Best Practices
        if (includeScreenshots) {
          try {
            onProgress?.({ step: 'screenshots', message: `Loading screenshots for ${raw.page_name || 'page'}...` });
            const shots = await fetchPageScreenshots(raw, 800);
            const available = [
              shots.desktop && { ...shots.desktop, label: 'Desktop View' },
              shots.tablet && { ...shots.tablet, label: 'Tablet View' },
              shots.mobile && { ...shots.mobile, label: 'Mobile View' },
            ].filter(Boolean);

            for (const img of available) {
              doc.addPage();
              y = 15;

              // Page name + device label as header
              doc.setFontSize(10);
              doc.setFont(undefined, 'bold');
              doc.setTextColor(51, 65, 85);
              doc.text(`${raw.page_name || 'Page'} - ${img.label}`, pageW / 2, y, { align: 'center' });
              doc.setTextColor(0, 0, 0);
              doc.setFont(undefined, 'normal');
              y += 6;

              // Scale image to fit within printable area
              const pxToMm = (px) => px / 3.78;
              let imgW = pxToMm(img.width);
              let imgH = pxToMm(img.height);
              const maxW = contentW;
              const maxH = pageH - y - 15;

              if (imgW > maxW) {
                const s = maxW / imgW;
                imgW = maxW;
                imgH = imgH * s;
              }
              if (imgH > maxH) {
                const s = maxH / imgH;
                imgH = maxH;
                imgW = imgW * s;
              }

              const xOffset = margin + (contentW - imgW) / 2;
              doc.addImage(img.dataUrl, 'JPEG', xOffset, y, imgW, imgH);
              y += imgH + 4;
            }

            // Reset y for next content after screenshots
            if (available.length > 0) {
              doc.addPage();
              y = 15;
            }
          } catch (err) {
            console.warn('[PDF Export] Screenshot embedding failed:', err.message);
          }
        }

        addSpace(2);
      }
    }

    // ---- ADDITIONAL RECOMMENDATIONS ----
    onProgress?.({ step: 'processing', message: 'Building recommendations...' });

    const recommendations = buildRecommendations(allRaw);
    if (recommendations.length > 0) {
      addHeading1('Additional Recommendations');
      recommendations.forEach(rec => addBullet(rec));
      addSpace(2);
    }

    // ---- PRIORITY ACTION ITEMS ----
    addHeading1('Priority Action Items');

    if (issuesByLevel.critical.length > 0) {
      addNormal('Before Launch - Must Fix:', { bold: true });
      issuesByLevel.critical.forEach((r, idx) => {
        const pageName = r.page_name || r.page_path || 'Unknown';
        const det = gatherIssueDetails(r);
        const summary = det.length > 0 ? det[0] : 'Critical issue detected';
        addBullet(`${pageName}: ${summary} (Issue #${idx + 1})`);
      });
      addSpace(2);
    }

    if (issuesByLevel.major.length > 0) {
      addNormal('High Priority:', { bold: true });
      const critOffset = issuesByLevel.critical.length;
      issuesByLevel.major.forEach((r, idx) => {
        const pageName = r.page_name || r.page_path || 'Unknown';
        const det = gatherIssueDetails(r);
        const summary = det.length > 0 ? det[0] : 'Major issue detected';
        addBullet(`${pageName}: ${summary} (Issue #${critOffset + idx + 1})`);
      });
      addSpace(2);
    }

    if (issuesByLevel.critical.length === 0 && issuesByLevel.major.length === 0) {
      addNormal('No critical or major issues found. The site is in good shape.');
    }

    // ---- RESPONSIVENESS CHECKS ----
    onProgress?.({ step: 'responsiveness', message: 'Building responsiveness checks...' });
    const respItems = buildResponsivenessCheckItems(allRaw);
    const respFailures = filterNegativesOnly(respItems, { includeWarnings: true });
    console.log('[PDF Export] Responsiveness (negatives only):', respFailures.length, '/', respItems.length);
    if (respFailures.length > 0) {
      addCheckSection('Responsiveness Checks', respFailures);
    } else {
      addHeading1('Responsiveness Checks');
      addNormal('✓ All responsiveness checks passed');
      addSpace(3);
    }

    // ---- ACCESSIBILITY CHECKS ----
    onProgress?.({ step: 'accessibility', message: 'Building accessibility checks...' });
    const a11yItems = buildAccessibilityCheckItems(allRaw);
    const a11yFailures = filterNegativesOnly(a11yItems, { includeWarnings: true });
    console.log('[PDF Export] Accessibility (negatives only):', a11yFailures.length, '/', a11yItems.length);
    if (a11yFailures.length > 0) {
      addCheckSection('Accessibility Checks', a11yFailures);
    } else {
      addHeading1('Accessibility Checks');
      addNormal('✓ All accessibility checks passed');
      addSpace(3);
    }

    // ---- SUMMARY STATISTICS ----
    onProgress?.({ step: 'summary-table', message: 'Building summary statistics...' });

    const stats = computeSummaryStatistics(allRaw);

    addHeading1('Summary Statistics');
    addSpace(1);

    addLabelValue('Total Pages Reviewed', String(stats.totalPages));
    addLabelValue('Pages Passed', `${stats.passedPages} (${stats.passRate}%)`);
    addLabelValue('Pages Failed', String(stats.failedPages));
    if (stats.warningPages > 0) {
      addLabelValue('Pages with Warnings', String(stats.warningPages));
    }
    addSpace(3);

    // Severity breakdown table
    addHeading2('Issues by Severity');
    addSpace(1);

    // Table header
    checkNewPage(30);
    doc.setFillColor(30, 41, 59);
    doc.rect(margin, y, contentW, 6, 'F');
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Severity Level', margin + 2, y + 4);
    doc.text('Count', margin + contentW - 30, y + 4);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
    y += 6;

    // Table rows
    const severityData = [
      ['Critical (Must Fix)', stats.totalCritical],
      ['Major (High Priority)', stats.totalMajor],
      ['Minor (Low Priority)', stats.totalMinor],
      ['Total Issues', stats.totalIssues],
    ];

    severityData.forEach((row, idx) => {
      checkNewPage(6);
      if (idx % 2 === 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(margin, y, contentW, 6, 'F');
      }
      doc.setFontSize(9);
      doc.setTextColor(55, 65, 81);
      if (idx === severityData.length - 1) doc.setFont(undefined, 'bold');
      doc.text(sanitizeText(row[0]), margin + 2, y + 4);
      doc.text(String(row[1]), margin + contentW - 30, y + 4);
      doc.setFont(undefined, 'normal');
      y += 6;
    });

    addSpace(3);

    // Category breakdown table
    addHeading2('Issues by Category');
    addSpace(1);

    // Table header
    checkNewPage(30);
    doc.setFillColor(30, 41, 59);
    doc.rect(margin, y, contentW, 6, 'F');
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Category', margin + 2, y + 4);
    doc.text('Pages Affected', margin + contentW - 30, y + 4);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
    y += 6;

    // Table rows
    const categoryData = Object.entries(stats.categories)
      .filter(([_, count]) => count > 0)
      .map(([category, count]) => [
        category.charAt(0).toUpperCase() + category.slice(1),
        count
      ]);

    categoryData.forEach((row, idx) => {
      checkNewPage(6);
      if (idx % 2 === 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(margin, y, contentW, 6, 'F');
      }
      doc.setFontSize(9);
      doc.setTextColor(55, 65, 81);
      doc.text(sanitizeText(row[0]), margin + 2, y + 4);
      doc.text(String(row[1]), margin + contentW - 30, y + 4);
      y += 6;
    });

    addSpace(4);

    // ---- REPORT FOOTER ----
    addSpace(4);
    addNormal(`Report Generated: ${prettyDate}`);
    addNormal(`Total Pages Reviewed: ${allRaw.length}`);

    onProgress?.({ step: 'finalizing', message: 'Finalizing document...' });

    // Add page numbers to all pages
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} of ${totalPages}  |  QA Automator by Elevated Audience  |  ${date}`,
        margin,
        pageH - 10
      );
    }

    onProgress?.({ step: 'saving', message: 'Saving PDF file...' });
    doc.save(`QA_Report_${projectName}_${date}.pdf`);

    onProgress?.({ step: 'complete', message: 'Export complete!' });
  },

  /**
   * Export project summary report as DOCX (Word) matching the QA Review Report template
   */
  exportProjectSummaryDOCX: async (project, results, rawResults, onProgress, opts = {}) => {
    const { generateQAReportDocx } = await import('./docxExport.js');
    await generateQAReportDocx(project, results, rawResults, onProgress, opts);
  },
};

// ============================================================
// ANALYTICS SERVICE - N8N Webhooks
// ============================================================

export const analyticsService = {
  /**
   * Get dashboard statistics
   * Uses Supabase direct connection (faster than n8n webhook)
   */
  getDashboardStats: async () => {
    try {
      console.log('[SUPABASE] GET dashboard analytics');

      // Get count of active projects
      const { count: totalProjects, error: projectsError } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .neq('status', 'archived');

      if (projectsError) throw projectsError;

      // Get count of all pages
      const { count: totalPagesQAd, error: pagesError } = await supabase
        .from('pages')
        .select('*', { count: 'exact', head: true });

      if (pagesError) throw pagesError;

      // Get all qa_results to calculate pass rate
      const { data: results, error: resultsError } = await supabase
        .from('qa_results')
        .select('overall_status');

      if (resultsError) throw resultsError;

      // Calculate pass rate
      const totalResults = results.length;
      const passedResults = results.filter(r => r.overall_status === 'PASS').length;
      const passRate = totalResults > 0 ? Math.round((passedResults / totalResults) * 100) : 0;

      // Calculate time saved (15 minutes per page = 0.25 hours)
      const avgTimeSaved = ((totalPagesQAd || 0) * 0.25).toFixed(1);

      return {
        totalProjects: totalProjects || 0,
        totalPagesQAd: totalPagesQAd || 0,
        passRate,
        avgTimeSaved: parseFloat(avgTimeSaved),
        recentActivity: []
      };
    } catch (error) {
      console.error('[SUPABASE Error] Failed to fetch dashboard stats:', error);
      // Return default stats on error
      return {
        totalProjects: 0,
        totalPagesQAd: 0,
        passRate: 0,
        avgTimeSaved: 0,
        recentActivity: []
      };
    }
  },

  /**
   * Get common issues from last 7 days
   * Uses Supabase direct connection to analyze qa_results
   */
  getCommonIssues: async (timeRange = '7d') => {
    try {
      console.log('[SUPABASE] GET common issues');

      // Calculate date threshold (7 days ago)
      const daysAgo = parseInt(timeRange);
      const threshold = new Date();
      threshold.setDate(threshold.getDate() - daysAgo);

      // Get all recent qa_results with page details
      const { data: results, error } = await supabase
        .from('qa_results')
        .select(`
          id, page_id, project_id, seo_checks, image_checks, link_checks, created_at,
          pages:page_id (page_name, page_url, page_path)
        `)
        .gte('created_at', threshold.toISOString());

      if (error) throw error;

      // Count issues and collect affected pages
      let missingAltText = 0;
      let metaDescMismatch = 0;
      let multipleH1Tags = 0;
      let brokenLinks = 0;

      const missingAltPages = [];
      const metaDescPages = [];
      const multipleH1Pages = [];
      const brokenLinksPages = [];

      results.forEach(result => {
        // Parse JSONB fields
        const seoChecks = typeof result.seo_checks === 'string'
          ? JSON.parse(result.seo_checks)
          : result.seo_checks || {};
        const imageChecks = typeof result.image_checks === 'string'
          ? JSON.parse(result.image_checks)
          : result.image_checks || {};
        const linkChecks = typeof result.link_checks === 'string'
          ? JSON.parse(result.link_checks)
          : result.link_checks || {};

        const pageInfo = {
          resultId: result.id,
          pageId: result.page_id,
          projectId: result.project_id,
          pageName: result.pages?.page_name || 'Unknown Page',
          pageUrl: result.pages?.page_url || '',
          pagePath: result.pages?.page_path || '',
        };

        // Count missing alt text
        if (imageChecks.missingAlt || imageChecks.missingAltText) {
          const count = imageChecks.missingAlt || imageChecks.missingAltText || 0;
          missingAltText += count;
          if (count > 0) {
            missingAltPages.push({ ...pageInfo, detail: `${count} image${count !== 1 ? 's' : ''} missing alt text` });
          }
        }

        // Count meta description mismatches
        if (seoChecks.metaDescMatch?.status === 'FAIL' || seoChecks.metaDescription?.status === 'FAIL') {
          metaDescMismatch++;
          metaDescPages.push({ ...pageInfo, detail: 'Meta description does not match expected' });
        }

        // Count multiple H1 tags
        if (seoChecks.h1Count?.count > 1) {
          multipleH1Tags++;
          multipleH1Pages.push({ ...pageInfo, detail: `${seoChecks.h1Count.count} H1 tags found` });
        }

        // Count broken links
        if (linkChecks.brokenCount || linkChecks.brokenLinks?.length) {
          const count = linkChecks.brokenCount || linkChecks.brokenLinks?.length || 0;
          brokenLinks += count;
          if (count > 0) {
            brokenLinksPages.push({ ...pageInfo, detail: `${count} broken link${count !== 1 ? 's' : ''}` });
          }
        }
      });

      return [
        { label: 'Missing Alt Text', count: missingAltText, type: 'images', pages: missingAltPages },
        { label: 'Meta Description Mismatch', count: metaDescMismatch, type: 'seo', pages: metaDescPages },
        { label: 'Multiple H1 Tags', count: multipleH1Tags, type: 'seo', pages: multipleH1Pages },
        { label: 'Broken Links', count: brokenLinks, type: 'links', pages: brokenLinksPages },
      ].sort((a, b) => b.count - a.count); // Sort by count descending
    } catch (error) {
      console.error('[SUPABASE Error] Failed to fetch common issues:', error);
      return [];
    }
  },

  /**
   * Get historical trends (pass rate, critical issues over time)
   * Uses qa_run_snapshots table
   */
  getHistoricalTrends: async (projectId, days = 30) => {
    try {
      const threshold = new Date();
      threshold.setDate(threshold.getDate() - days);

      let query = supabase
        .from('qa_run_snapshots')
        .select('project_id, run_at, total_pages, passed_pages, failed_pages, warning_pages, pass_rate, critical_issues_total')
        .gte('run_at', threshold.toISOString())
        .order('run_at', { ascending: true });

      if (projectId) {
        query = query.eq('project_id', projectId);
      }

      const { data, error } = await query;
      if (error) throw error;

      if (!projectId) {
        const byDate = new Map();
        (data || []).forEach(row => {
          const date = new Date(row.run_at).toISOString().slice(0, 10);
          const existing = byDate.get(date);
          if (!existing) {
            byDate.set(date, {
              date,
              totalPages: row.total_pages || 0,
              passedPages: row.passed_pages || 0,
              failedPages: row.failed_pages || 0,
              warningPages: row.warning_pages || 0,
              passRate: parseFloat(row.pass_rate) || 0,
              criticalIssues: row.critical_issues_total || 0,
            });
          } else {
            existing.totalPages += row.total_pages || 0;
            existing.passedPages += row.passed_pages || 0;
            existing.failedPages += row.failed_pages || 0;
            existing.warningPages += row.warning_pages || 0;
            existing.criticalIssues += row.critical_issues_total || 0;
          }
        });
        return Array.from(byDate.values()).map(r => ({
          ...r,
          passRate: r.totalPages > 0 ? Math.round((r.passedPages / r.totalPages) * 100) : 0,
        }));
      }

      return (data || []).map(row => ({
        date: new Date(row.run_at).toISOString().slice(0, 10),
        totalPages: row.total_pages || 0,
        passedPages: row.passed_pages || 0,
        failedPages: row.failed_pages || 0,
        warningPages: row.warning_pages || 0,
        passRate: parseFloat(row.pass_rate) || 0,
        criticalIssues: row.critical_issues_total || 0,
      }));
    } catch (error) {
      console.error('[SUPABASE Error] Failed to fetch historical trends:', error);
      return [];
    }
  },

  /**
   * Get regression alerts (pages that previously passed and now fail)
   * Uses regression_alerts table
   */
  getRegressionAlerts: async (projectId) => {
    try {
      let query = supabase
        .from('regression_alerts')
        .select('id, project_id, page_id, detected_at, previous_status, current_status')
        .order('detected_at', { ascending: false });

      if (projectId) {
        query = query.eq('project_id', projectId);
      }

      const { data: alerts, error } = await query;
      if (error) throw error;
      if (!alerts?.length) return [];

      const pageIds = [...new Set(alerts.map(a => a.page_id))];
      const { data: pages } = await supabase.from('pages').select('id, page_name, page_url').in('id', pageIds);
      const pageMap = Object.fromEntries((pages || []).map(p => [p.id, p]));

      return alerts.map(row => ({
        id: row.id,
        projectId: row.project_id,
        pageId: row.page_id,
        detectedAt: row.detected_at,
        previousStatus: row.previous_status,
        currentStatus: row.current_status,
        pageName: pageMap[row.page_id]?.page_name || 'Unknown',
        pageUrl: pageMap[row.page_id]?.page_url || '',
      }));
    } catch (error) {
      console.error('[SUPABASE Error] Failed to fetch regression alerts:', error);
      return [];
    }
  },

  /**
   * Get count of regression alerts for dashboard badge
   */
  getRegressionAlertsCount: async (projectId) => {
    try {
      let query = supabase
        .from('regression_alerts')
        .select('*', { count: 'exact', head: true });

      if (projectId) {
        query = query.eq('project_id', projectId);
      }

      const { count, error } = await query;
      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('[SUPABASE Error] Failed to fetch regression alerts count:', error);
      return 0;
    }
  },
};

// ============================================================
// SETTINGS SERVICE (Local Storage for now)
// ============================================================

export const settingsService = {
  /**
   * Get settings from local storage
   */
  getSettings: async () => {
    const stored = localStorage.getItem('qaSettings');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return getDefaultSettings();
      }
    }
    return getDefaultSettings();
  },

  /**
   * Save settings to local storage
   */
  updateSettings: async (settings) => {
    localStorage.setItem('qaSettings', JSON.stringify(settings));
    return settings;
  },
};

const getDefaultSettings = () => ({
  screenshotQuality: 'standard',
  emailOnCritical: true,
  emailOnComplete: true,
  weeklyReports: false,
  notificationEmail: ''
});

// ============================================================
// AUTHENTICATION SERVICE
// ============================================================

export const authService = {
  /**
   * Login user
   */
  login: async (email, password) => {
    try {
      console.log('🔐 Attempting login for:', email);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Fetch user profile with role
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Profile fetch error:', profileError);
      }

      const user = {
        id: data.user.id,
        email: data.user.email,
        role: profile?.role || 'user',
        name: profile?.name || email.split('@')[0],
        avatar: profile?.avatar || null,
        created_at: profile?.created_at || data.user.created_at,
      };

      console.log('✅ Login successful:', user);
      return { user, session: data.session };
    } catch (error) {
      console.error('❌ Login error:', error);
      throw error;
    }
  },

  /**
   * Register new user (admin only via admin panel)
   */
  register: async (email, password, name, role = 'user') => {
    try {
      console.log('👤 Creating new user:', email);

      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (authError) throw authError;

      // Create user profile
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .insert([
          {
            id: authData.user.id,
            email,
            name,
            role,
            created_at: new Date().toISOString(),
          },
        ])
        .select()
        .single();

      if (profileError) throw profileError;

      console.log('✅ User created:', profile);
      return profile;
    } catch (error) {
      console.error('❌ Registration error:', error);
      throw error;
    }
  },

  /**
   * Logout user
   */
  logout: async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      console.log('✅ Logout successful');
    } catch (error) {
      console.error('❌ Logout error:', error);
      throw error;
    }
  },

  /**
   * Get current session
   */
  getSession: async () => {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;

      if (data.session) {
        // Fetch user profile
        const { data: profile } = await supabase
          .from('users')
          .select('*')
          .eq('id', data.session.user.id)
          .single();

        const user = {
          id: data.session.user.id,
          email: data.session.user.email,
          role: profile?.role || 'user',
          name: profile?.name || data.session.user.email.split('@')[0],
          avatar: profile?.avatar || null,
          created_at: profile?.created_at || data.session.user.created_at,
        };

        return { user, session: data.session };
      }

      return { user: null, session: null };
    } catch (error) {
      console.error('Session error:', error);
      return { user: null, session: null };
    }
  },

  /**
   * Get all users (admin only)
   */
  getUsers: async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Get users error:', error);
      throw error;
    }
  },

  /**
   * Update user role (admin only)
   */
  updateUserRole: async (userId, role) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .update({ role })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      console.log('✅ User role updated:', data);
      return data;
    } catch (error) {
      console.error('❌ Update role error:', error);
      throw error;
    }
  },

  /**
   * Delete user (admin only)
   */
  deleteUser: async (userId) => {
    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);

      if (error) throw error;
      console.log('✅ User deleted');
    } catch (error) {
      console.error('❌ Delete user error:', error);
      throw error;
    }
  },
};

// ============================================================
// REAL-TIME UPDATES (Polling)
// ============================================================

export const realtimeService = {
  /**
   * Poll for project status updates
   * Returns a function to stop polling
   */
  subscribeToProjectStatus: (projectId, callback, intervalMs = 5000) => {
    let isActive = true;
    const startedAt = Date.now();
    const MAX_POLL_DURATION = 30 * 60 * 1000; // 30 minutes safety net

    const poll = async () => {
      if (!isActive) return;

      // Safety net: stop polling after MAX_POLL_DURATION
      if (Date.now() - startedAt > MAX_POLL_DURATION) {
        console.warn(`[Polling] Max duration (30min) reached for project ${projectId}. Stopping.`);
        isActive = false;
        try {
          const status = await qaService.getQAStatus(projectId);
          status._pollingTimedOut = true;
          callback(status);
        } catch (e) { /* ignore */ }
        return;
      }

      try {
        const status = await qaService.getQAStatus(projectId);
        callback(status);
      } catch (error) {
        console.error('Polling error:', error);
      }

      if (isActive) {
        setTimeout(poll, intervalMs);
      }
    };

    poll();

    // Return unsubscribe function
    return () => {
      isActive = false;
    };
  },

  /**
   * Poll for results updates
   */
  subscribeToResults: (projectId, callback, intervalMs = 10000) => {
    let isActive = true;

    const poll = async () => {
      if (!isActive) return;

      try {
        const results = await resultsService.getProjectResults(projectId);
        callback(results);
      } catch (error) {
        console.error('Polling error:', error);
      }

      if (isActive) {
        setTimeout(poll, intervalMs);
      }
    };

    poll();

    return () => {
      isActive = false;
    };
  },

  /**
   * Subscribe to real-time changes for a specific page using Supabase Realtime.
   * Listens for UPDATE events on the `pages` table and INSERT/UPDATE on `qa_results`.
   * Returns an unsubscribe function to remove the channel.
   *
   * NOTE: Requires Supabase Realtime to be enabled for `pages` and `qa_results`
   *       tables in the Supabase Dashboard (Database > Replication).
   */
  subscribeToPageChanges: (pageId, { onPageUpdate, onResultsReady }) => {
    const channel = supabase
      .channel(`page-rerun-${pageId}-${Date.now()}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'pages',
        filter: `id=eq.${pageId}`,
      }, (payload) => {
        console.log('[Realtime] Page update:', payload.new?.status);
        onPageUpdate?.(payload.new);
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'qa_results',
        filter: `page_id=eq.${pageId}`,
      }, (payload) => {
        console.log('[Realtime] QA results ready for page:', pageId);
        onResultsReady?.(payload.new);
      })
      .subscribe((status) => {
        console.log('[Realtime] Channel status:', status);
      });

    // Return unsubscribe function
    return () => {
      supabase.removeChannel(channel);
    };
  },

  /**
   * Cancel the currently active Playwright job
   */
  cancelActiveJob: async () => {
    try {
      const playwrightServiceUrl = import.meta.env.VITE_PLAYWRIGHT_SERVICE_URL || 'http://localhost:3001';
      const response = await axios.post(`${playwrightServiceUrl}/cancel-job`, {}, {
        timeout: 10000
      });
      return response.data;
    } catch (error) {
      console.error('[API] Failed to cancel job:', error);
      throw error;
    }
  },
};

// ============================================================
// DEFAULT EXPORT
// ============================================================

export default {
  auth: authService,
  projects: projectService,
  pages: pageService,
  qa: qaService,
  results: resultsService,
  export: exportService,
  analytics: analyticsService,
  settings: settingsService,
  realtime: realtimeService,
};
